// 홈 메인 화면 — AI 프롬프트 + ProActive 제안 카드 + 대화 전환 흐름 (poc-portal Home.startview 이식판)
//
// 이식 시 바뀐 지점 셋:
//  - react-router(useNavigate/useLocation) → lib/navigation의 navigate
//  - "새 AI 대화" 신호를 location.state 대신 Sidebar가 쏘는 navNewAiChat 이벤트로 받는다
//  - AX 컴포넌트를 yukimushi-component-fe 패키지에서 가져온다
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AXKeyValueGrid,
  AXProgressBar,
  AXSegmentedControl,
  AXStatusBadge,
  AXStepProgress,
  AXTagChip,
} from 'yukimushi-component-fe';
import { navigate } from '../../lib/navigation';
import { useChatContext } from '../../context/ChatContext';
import { deleteDoc, newConversationId, streamChat } from '../../api/aiChat';
import { fetchProactive } from '../../api/portal';
import { isImageFile, isSupportedImage, prepareChatImage } from '../../utils/image';
import LLMSelector from '../../components/Home/LLMSelector';
import { PostSuggestCard, ProActiveSlotCard } from './Home.cards';
import { ActionModal, MailBodyViewer, RepaymentScheduleModal } from './Home.modals';
import {
  AttachmentStrip,
  ingestAttachments,
  newAttachmentUid,
  toAttachments,
} from './Home.attachments';
import { LiveResponseBody } from './Home.response';
import {
  AR_STATUS_STYLE,
  ATTACHMENT_PREVIEWS,
  DOC_PREVIEWS,
  DURATION,
  IMAGE_TURN_LIMIT,
  INITIAL_PROACTIVE_CARDS,
  JOURNAL_LINK_PATH,
  MAX_CHAT_IMAGES,
  PROACTIVE_CARD_QUEUE,
  mapProactiveItems,
  postHintChips,
  postResponseSuggestions,
} from './Home.data';
import { SLOT_RESPONSE_DATA } from './Home.slotResponses';
import type {
  ActionModalConfig,
  ConvPhase,
  ConvSlot,
  ConversationAttachment,
  PostSuggestItem,
  ProActiveCardDef,
  SlotBubblePhase,
  SlotPhase,
} from './Home.types';
import styles from './Home.startview.module.css';

export default function Home() {
  const {
    chatState, query, turns, setQuery,
    enterFocused, exitFocused, cancelResponse, textareaRef,
  } = useChatContext();
  const submittedQuery = turns[turns.length - 1]?.trigger.label ?? '';
  /* navigate는 lib/navigation의 모듈 함수 — 훅으로 얻지 않는다 */
  const isFocused = chatState === 'focused';

  /* 이전 방문 여부 */
  const wasVisited = sessionStorage.getItem('homeVisited') === 'true';

  const [activeIdx, setActiveIdx]       = useState(() => parseInt(sessionStorage.getItem('homeActiveIdx') ?? '0'));
  const [busy, setBusy]                 = useState(false);
  const [entered, setEntered]           = useState(wasVisited);
  const [, setHeights]                  = useState<number[]>([]);
  const [, setGreetingDone]             = useState(wasVisited);

  /* 대화 전환 페이즈 */
  type BubblePhase = 'hidden' | 'loading' | 'typing' | 'complete';
  const BUBBLE_TEXT = '세금계산서 발행, 독촉장 작성, 거래처 미팅 준비 3가지를 제안드려요. 어떤 것부터 시작할까요?';

  const savedConvPhase      = sessionStorage.getItem('homeConvPhase') as ConvPhase | null;
  const isRestoredSession   = useRef(!!savedConvPhase && savedConvPhase !== 'idle');

  const [convPhase, setConvPhase]           = useState<ConvPhase>(savedConvPhase || 'idle');
  const [newQuery, setNewQuery]             = useState('');
  const [, setNewPromptFocused] = useState(false);
  const [erpDone, setErpDone]               = useState(savedConvPhase === 'done');
  const [greetingCollapsed, setGreetingCollapsed] = useState(sessionStorage.getItem('homeGreetingCollapsed') === 'true');
  const [proActiveFromHome, setProActiveFromHome] = useState(false); // 홈 화면에서 ProActive 카드 클릭 시 인사말/프롬프트 페이드아웃 트리거
  const [openActionModal, setOpenActionModal] = useState<ActionModalConfig | null>(null);
  const [section0Collapsed, setSection0Collapsed] = useState(false); // 페이드아웃 후 section 0 높이 collapse
  const [isConvMode, setIsConvMode] = useState(false); // 대화 모드 (단일 스크롤 + 플로팅 프롬프트)
  const [scrolledDown, setScrolledDown] = useState(false); // 스크롤 다운 여부 — scrollHintDown 표시 제어
  const convScrollRef = useRef<HTMLDivElement>(null);
  const isConvModeRef = useRef(false);
  const prevDoneCountRef = useRef(0); // 자동 스크롤: 직전 done 슬롯 수 추적

  /* 말풍선 페이즈 */
  const [bubblePhase, setBubblePhase] = useState<BubblePhase>(
    (sessionStorage.getItem('homeBubblePhase') as BubblePhase) || 'hidden'
  );
  const [bubbleText,  setBubbleText]  = useState(sessionStorage.getItem('homeBubbleText') || '');
  const bubbleTyperRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /* ── 메일 원문 우측 슬라이드 패널 ([메일 ▶ ...] 칩 클릭) ── */
  const [mailSidePanelOpen, setMailSidePanelOpen] = useState(false);
  const [mailSidePanelLang, setMailSidePanelLang] = useState<'ko' | 'ja'>('ko');

  /* ── 문서 미리보기 우측 패널 (ai-doc-preview-btn 클릭) ── */
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [docPreviewIdx, setDocPreviewIdx] = useState(0);

  /* ── 첨부 파일 원본 이미지 우측 패널 (영수증 원본 확인 / 리스 계약서 첨부 클릭) ── */
  const [attachmentPreview, setAttachmentPreview] = useState<keyof typeof ATTACHMENT_PREVIEWS | null>(null);

  /* ── 상환 스케줄 확인 팝업 (leaseInfo [상환 스케줄 확인] 클릭) ── */
  const [repaymentScheduleOpen, setRepaymentScheduleOpen] = useState(false);

  /* ── MOU 메일 원문 오버레이 ── */
  const [mouMailSourceOpen, setMouMailSourceOpen] = useState(false);
  const [mouMailSourceLang, setMouMailSourceLang] = useState<'ko' | 'ja'>('ja');

  /* ── MOU 초안 패널 ── */
  const [mouPanelOpen, setMouPanelOpen] = useState(false);
  const [mouPanelStep, setMouPanelStep] = useState<0|1|2|3|4>(0); // 0=준비, 1~4=진행중
  const [mouDocFull, setMouDocFull] = useState(false);
  const [mouDocReveal, setMouDocReveal] = useState(false);
  const [mouGenTitle, setMouGenTitle] = useState('문서 캔버스를 준비하고 있습니다');
  const [mouGenDesc, setMouGenDesc] = useState('ONECHAMBER 참고자료와 메일 요청사항을 검토한 뒤, 적합한 MOU 구조를 구성합니다.');
  const [mouGenStatus, setMouGenStatus] = useState('참고자료 분석 중');
  const [mouGenFadeOut, setMouGenFadeOut] = useState(false);
  const mouPanelTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const mouAnimationStartedRef = useRef(false); // 타이머 중복 실행 방지

  /* ── MOU 회신 메일 모달 ── */
  const [mouReplyOpen, setMouReplyOpen] = useState(false);
  const [mouReplyPhase, setMouReplyPhase] = useState<'loading' | 'ready'>('loading');
  const [mouReplyLang, setMouReplyLang] = useState<'ko' | 'ja'>('ko');
  const [mouReplyEditing, setMouReplyEditing] = useState(false);
  const [mouReplyHint, setMouReplyHint] = useState('문서를 PDF로 변환하고 회신 초안을 작성하는 중...');
  const [mouReplyKoText, setMouReplyKoText] = useState(
`타카하시 켄지 본부장님께,

안녕하세요.
Enterprise AI Platform 글로벌 사업개발 담당 김민준입니다.

요청해 주신 Enterprise AI 및 Sovereign AI 협력을 위한 MOU 초안을 작성하여 첨부드립니다.

말씀해 주신 아래 항목을 중심으로 초안에 반영했습니다.

1. 양사의 역할과 책임 범위
2. 일본 시장 공동 영업 및 PoC 추진 방식
3. 고객 데이터 보관 위치와 보안·컴플라이언스 기준
4. 회계·세무 자동화 영역의 주요 레퍼런스
5. 향후 기술 검증 및 공동 웨비나 추진 일정

특히 일본 내 데이터 주권을 고려한 보관 구조와 회계·세무 업무 자동화 레퍼런스를 함께 정리했습니다.
MOU 각 조항은 귀사 내부 검토 의견에 따라 유연하게 조정 가능합니다.

가능하시다면 다음 주 중 화상회의를 통해 구체적인 협력 범위를 논의드리고자 합니다.
편하신 후보 일정을 회신해 주시면 조율하겠습니다.

검토 부탁드리며, 회신 기다리겠습니다.

감사합니다.

김민준 드림
Enterprise AI Platform · 글로벌 사업개발`
  );
  const [mouReplyToastVisible, setMouReplyToastVisible] = useState(false);
  const mouReplyTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  /* ── 보고서 패널 ── */
  const [reportPanelOpen, setReportPanelOpen] = useState(false);
  const [reportPanelStep, setReportPanelStep] = useState<0|1|2|3|4>(0);
  const [reportDocFull, setReportDocFull] = useState(false);
  const [reportDocReveal, setReportDocReveal] = useState(false);
  const [reportGenTitle, setReportGenTitle] = useState('문서 캔버스를 준비하고 있습니다');
  const [reportGenDesc, setReportGenDesc] = useState('ERP 회계 거래내역에서 미수금 현황을 분석해 보고서 구조를 구성합니다.');
  const [reportGenStatus, setReportGenStatus] = useState('거래내역 분석 중');
  const [reportGenFadeOut, setReportGenFadeOut] = useState(false);
  const reportPanelTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const reportAnimationStartedRef = useRef(false);

  /* ── ProActive FAB + Drawer ── */
  const [aiPaDrawerOpen, setAiPaDrawerOpen] = useState(false);

  /* ── 대화 첨부 ──
     문서와 이미지를 한 목록으로 다룬다. 둘 다 대화에 붙어 있는 것이고(색인되어 검색 대상),
     전송해도 사라지지 않으며 사용자가 × 로 뺄 때 색인도 함께 내려간다.
     종전에는 문서 칩이 전송 시 버블로 옮겨가 뺄 방법이 없었고, 화면에서 지워도 색인이 남았다. */
  const [attachments, setAttachments] = useState<ConversationAttachment[]>([]);
  const [attachNotice, setAttachNotice] = useState<string | null>(null);
  const attachmentsRef = useRef<ConversationAttachment[]>([]);
  attachmentsRef.current = attachments;

  const [attachDropdownOpen, setAttachDropdownOpen] = useState<'home' | 'conv' | number | null>(null);
  const homeFileInputRef = useRef<HTMLInputElement>(null);
  const convFileInputRef = useRef<HTMLInputElement>(null);

  /* ── RAG 실서비스 연동 (메인 검색창 전용) ── */
  const conversationIdRef = useRef(newConversationId());

  /* 이 턴에 이미지를 실어 보냈다 — 사용 횟수를 올린다.
     이미지는 원본이 매 턴 전송돼 토큰이 붙으므로 몇 턴 뒤에는 채팅에 싣기를 멈춘다.
     다만 목록에서 지우지는 않는다 — 색인은 남아 있어 내용 질문은 검색으로 답하고,
     사용자가 직접 뺄 때까지 "이 대화에 붙어 있다"는 표시는 유지해야 한다. */
  const consumeImageTurn = useCallback(() => {
    setAttachments(prev => prev.map(a =>
      a.kind === 'image' && a.image ? { ...a, image: { ...a.image, turns: a.image.turns + 1 } } : a));
  }, []);

  /**
   * 첨부 취소 — 목록에서 빼고 색인도 내린다.
   * 색인 삭제가 실패하면 목록에 되돌린다: 지운 줄 알았는데 계속 근거로 잡히는 것보다
   * 남아 있다고 정직하게 보여주는 편이 낫다.
   */
  const removeAttachment = useCallback(async (uid: string) => {
    setAttachNotice(null);
    const target = attachmentsRef.current.find(a => a.uid === uid);
    if (!target) {
      return;
    }
    setAttachments(prev => prev.filter(a => a.uid !== uid));
    if (!target.docId) {
      return;   // 아직 색인 전(또는 실패) — 내릴 것이 없다
    }
    try {
      await deleteDoc(target.docId, conversationIdRef.current);
    } catch (e) {
      setAttachments(prev => (prev.some(a => a.uid === uid) ? prev : [...prev, target]));
      setAttachNotice(e instanceof Error && /[가-힣]/.test(e.message) ? e.message : '첨부를 취소하지 못했습니다');
    }
  }, []);

  /**
   * 첨부 처리 — 문서와 이미지 모두 색인하고, 이미지는 채팅 요청용 축소본도 만든다.
   * 색인에는 축소본이 아닌 원본 File을 넘긴다: 전사 정확도가 원본에서 낫고,
   * 색인 title이 원본 파일명이어야 [출처: …] 칩이 제대로 뜬다.
   */
  const attachFiles = useCallback((rawFiles: File[]) => {
    const images = rawFiles.filter(isImageFile);
    const docs = rawFiles.filter(f => !isImageFile(f));
    const unsupported = images.filter(f => !isSupportedImage(f));
    const usable = images.filter(isSupportedImage);

    const notices: string[] = [];
    if (unsupported.length > 0) {
      notices.push('이미지는 PNG·JPEG·WebP·GIF만 첨부할 수 있습니다');
    }
    const imageRoom = MAX_CHAT_IMAGES - attachmentsRef.current.filter(a => a.kind === 'image').length;
    const acceptedImages = usable.slice(0, Math.max(0, imageRoom));
    if (usable.length > imageRoom) {
      notices.push(`이미지는 ${MAX_CHAT_IMAGES}장까지 첨부할 수 있습니다`);
    }
    setAttachNotice(notices.length > 0 ? notices.join(' · ') : null);

    const toIngest = [...docs, ...acceptedImages];
    if (toIngest.length === 0) {
      return;
    }
    const entries = toAttachments(toIngest);
    setAttachments(prev => [...prev, ...entries]);
    ingestAttachments(toIngest, entries, conversationIdRef.current, applyIngest);

    // 이미지는 채팅 요청에도 실어야 하므로 축소본을 따로 만든다(색인과 별개 경로)
    acceptedImages.forEach(file => {
      const entry = entries.find(e => e.name === file.name && e.kind === 'image');
      prepareChatImage(file)
        .then(img => setAttachments(prev => prev.map(a => a.uid === entry?.uid
          ? { ...a, previewUrl: img.previewUrl, image: { mimeType: img.mimeType, data: img.data, turns: 0 } }
          : a)))
        .catch(() => setAttachNotice(`'${file.name}' 이미지를 불러오지 못했습니다`));
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* 색인 진행 반영 — 완료 시 docId를 받아 둔다(취소할 때 이 값으로 색인을 내린다) */
  const applyIngest = useCallback((uid: string, ingest: ConversationAttachment['ingest'], docId?: string) => {
    setAttachments(prev => prev.map(a => a.uid === uid
      ? { ...a, ingest, ...(docId ? { docId } : {}) }
      : a));
  }, []);

  const openMouReply = () => {
    mouReplyTimersRef.current.forEach(clearTimeout);
    mouReplyTimersRef.current = [];
    setMouReplyPhase('loading');
    setMouReplyLang('ko');
    setMouReplyEditing(false);
    setMouReplyHint('문서를 PDF로 변환하고 회신 초안을 작성하는 중...');
    setMouReplyOpen(true);
    const t1 = setTimeout(() => {
      setMouReplyPhase('ready');
      setMouReplyHint('AI 초안 · 검토 후 발송하세요');
    }, 2200);
    mouReplyTimersRef.current = [t1];
  };

  const closeMouReply = () => {
    mouReplyTimersRef.current.forEach(clearTimeout);
    mouReplyTimersRef.current = [];
    setMouReplyOpen(false);
  };

  const sendMouReply = () => {
    setMouReplyHint('일본어로 번역하여 발송하고 있어요...');
    const t = setTimeout(() => {
      closeMouReply();
      setMouReplyToastVisible(true);
      setTimeout(() => setMouReplyToastVisible(false), 3200);
    }, 950);
    mouReplyTimersRef.current.push(t);
  };

  /* ── 보고서 패널 열기/닫기 ── */
  const openReportPanel = () => {
    if (reportAnimationStartedRef.current) return;
    reportAnimationStartedRef.current = true;
    setMouPanelOpen(false);
    setMailSidePanelOpen(false);
    setDocPreviewOpen(false);
    setAttachmentPreview(null);
    setAiPaDrawerOpen(false);
    setReportDocFull(false);
    setReportDocReveal(false);
    setReportGenFadeOut(false);
    setReportPanelStep(1);
    setReportGenTitle('문서 캔버스를 준비하고 있습니다');
    setReportGenDesc('ERP 회계 거래내역에서 미수금 현황을 분석해 보고서 구조를 구성합니다.');
    setReportGenStatus('거래내역 분석 중');
    setReportPanelOpen(true);

    const t1 = setTimeout(() => {
      setReportPanelStep(2);
      setReportGenTitle('미수금 거래처를 집계하고 있습니다');
      setReportGenDesc('연체·마감 임박 거래처를 선별하고 금액을 합산합니다.');
      setReportGenStatus('거래처 선별·집계 중');
    }, 1100);
    const t2 = setTimeout(() => {
      setReportPanelStep(3);
      setReportGenTitle('보고서를 구성하고 있습니다');
      setReportGenDesc('거래처별 표와 요약, 권장 조치를 문서로 정리합니다.');
      setReportGenStatus('표·요약 구성 중');
    }, 2300);
    const t3 = setTimeout(() => {
      setReportPanelStep(4);
      setReportGenTitle('초안을 마무리하고 있습니다');
      setReportGenDesc('제출 가능한 형태로 보고서 초안을 정리합니다.');
      setReportGenStatus('초안 정리 중');
    }, 3500);
    const t4 = setTimeout(() => {
      setReportGenFadeOut(true);
      setTimeout(() => {
        setReportDocFull(true);
        setTimeout(() => setReportDocReveal(true), 30);
      }, 260);
    }, 5000);
    reportPanelTimersRef.current = [t1, t2, t3, t4];
  };

  const closeReportPanel = () => {
    reportPanelTimersRef.current.forEach(clearTimeout);
    reportPanelTimersRef.current = [];
    reportAnimationStartedRef.current = false;
    setReportPanelOpen(false);
    setReportPanelStep(0);
    setReportDocFull(false);
    setReportDocReveal(false);
    setReportGenFadeOut(false);
  };

  /* ── 초기 응답 헤더 단계 시퀀서 (convPhase 레벨) ── */
  const [initHdrTitle, setInitHdrTitle] = useState<string | null>(null);
  const [initHdrStepIdx, setInitHdrStepIdx] = useState(0);
  const [initHdrStepTotal, setInitHdrStepTotal] = useState(4);
  const [initHdrDoneGreen, setInitHdrDoneGreen] = useState(false);
  const initStepStartedRef = useRef(false);
  /* 슬롯 헤더 단계 시퀀서 — slot.id로 중복 실행 방지 */
  const slotStepStartedRef = useRef<Set<number>>(new Set());

  /* ── AI 대화 탭별 세션 관리 ── */
  const [activeChatId, setActiveChatId] = useState<string | null>(null);
  const activeChatIdRef = useRef<string | null>(null);
  useEffect(() => { activeChatIdRef.current = activeChatId; }, [activeChatId]);

  /* 세션 스냅샷 저장소 (탭 ID → 대화 상태) */
  interface ChatSession {
    slots: ConvSlot[];
    convPhase: ConvPhase;
    isConvMode: boolean;
    section0Collapsed: boolean;
    greetingCollapsed: boolean;
    proActiveFromHome: boolean;
    currentProActiveCards: ProActiveCardDef[];
    /* 탭별 RAG 대화 컨텍스트 ID — 이게 없으면 탭 복귀 시 화면(UI)만 복원되고
       streamChat·uploadDocs는 마지막으로 발급된 다른 탭의 ID로 전송되어
       서버 멀티턴 맥락·대화 범위 색인 문서가 탭과 어긋난다 */
    conversationId: string;
    /* 이 탭에 붙인 자료 — 색인은 대화 범위로 서버에 남아 있으므로 탭을 오가도 표시가
       유지돼야 한다. 여기(메모리 Map)에 담으므로 이미지 base64도 그대로 살아 있다.
       다만 새로고침하면 사라진다 — 색인은 남아 검색은 되지만 배지는 다시 안 보인다 */
    attachments: ConversationAttachment[];
  }
  const chatSessionsRef = useRef<Map<string, ChatSession>>(new Map());

  /* ── 다중 대화 슬롯 ── */
  const [slots, setSlots] = useState<ConvSlot[]>([]);
  const sectionCountRef   = useRef(2);

  /* ── ProActive 마지막 섹션 카드 (항상 최신 상태 유지) ── */
  const [currentProActiveCards, setCurrentProActiveCards] = useState<ProActiveCardDef[]>(INITIAL_PROACTIVE_CARDS);
  /** setState 밖에서 현재 목록을 읽기 위한 ref 미러 */
  const currentProActiveCardsRef = useRef<ProActiveCardDef[]>(INITIAL_PROACTIVE_CARDS);
  const fetchedProActiveCardsRef = useRef<ProActiveCardDef[]>(INITIAL_PROACTIVE_CARDS);
  /* 큐 포인터: 다음에 보충할 카드의 PROACTIVE_CARD_QUEUE 인덱스 */
  const queuePointerRef = useRef<number>(PROACTIVE_CARD_QUEUE.length);
  useEffect(() => {
    fetchProactive()
      .then(items => {
        const mapped = mapProactiveItems(items);
        if (mapped.length > 0) {
          fetchedProActiveCardsRef.current = mapped;
          /* 큐 전체 표시, 포인터는 끝으로 */
          queuePointerRef.current = mapped.length;
          const initial = mapped.slice();
          currentProActiveCardsRef.current = initial;
          setCurrentProActiveCards(initial);
        }
      })
      .catch(console.error);
  }, []);
  const slotBubbleRefs    = useRef<Map<number, { t1: ReturnType<typeof setTimeout> | null; interval: ReturnType<typeof setInterval> | null; tIdle: ReturnType<typeof setTimeout> | null }>>(new Map());
  const slotBubbleRowRefs = useRef<Map<number, HTMLDivElement | null>>(new Map()); // nextActionRow 스크롤 타겟
  const journalSentMsgRefs = useRef<Map<number, HTMLDivElement | null>>(new Map()); // 전표 전송 완료 AI 메시지 스크롤 타겟 (slot.id로 조회)

  /* ── 슬롯 응답 연출 타이머 — 홈 복귀/세션 전환 시 일괄 취소용 ── */
  const slotFlowTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const clearSlotFlowTimers = () => {
    slotFlowTimersRef.current.forEach(clearTimeout);
    slotFlowTimersRef.current = [];
    slotBubbleRefs.current.forEach(r => {
      if (r.t1) clearTimeout(r.t1);
      if (r.interval) clearInterval(r.interval);
      if (r.tIdle) clearTimeout(r.tIdle);
    });
    slotBubbleRefs.current.clear();
  };

  /* ── 세션 저장용 슬롯 정규화 — 진행 중이던 연출을 완료 상태로 변환 ── */
  const normalizeSlotsForSave = (list: ConvSlot[]): ConvSlot[] =>
    list.map(s => {
      const phase: SlotPhase = s.phase === 'streaming' ? 'done' : s.phase;
      let bubblePhase: SlotBubblePhase = (s.bubblePhase === 'loading' || s.bubblePhase === 'typing') ? 'complete' : s.bubblePhase;
      let bubbleText = s.bubbleText;
      /* 말풍선 연출이 끝나기 전 이탈한 슬롯은 전체 문구로 보정 (live 슬롯은 더미 문구 주입 방지) */
      if (phase === 'done' && !s.live) {
        const full = (SLOT_RESPONSE_DATA[s.responseKey] ?? SLOT_RESPONSE_DATA.custom).bubbleText;
        if (full) { bubblePhase = 'complete'; bubbleText = full; }
      }
      /* erpDone(모듈 칩 활성화)·headerDoneGreen(완료 타이틀 초록색)도 타이머 의존 — done 슬롯은 완료 처리 */
      return phase === 'done'
        ? { ...s, phase, bubblePhase, bubbleText, erpDone: true, headerDoneGreen: true }
        : { ...s, phase, bubblePhase, bubbleText };
    });

  const skipAnim      = useRef(wasVisited);
  const isFirstSend   = useRef(!isRestoredSession.current);

  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef     = useRef<HTMLDivElement>(null);
  const sectionRefs  = useRef<(HTMLDivElement | null)[]>([]);
  const cardRef      = useRef<HTMLDivElement>(null);
  const newTextareaRef = useRef<HTMLTextAreaElement>(null);
  const postChipsRef   = useRef<HTMLDivElement>(null);
  /* 한글/일본어 등 IME 조합 중 Enter 전송 방지용 플래그.
     조합 중(스페이스 등으로 확정되기 전) Enter를 누르면 아직 커밋되지 않은
     마지막 글자만 남고 그 앞 텍스트가 유실되는 문제가 있어, 조합 중에는
     Enter 전송을 무시하고 IME가 조합을 확정하도록 둔다. */
  const isComposingRef = useRef(false);
  const goToRef        = useRef<(n: number) => void>(() => {});
  const activeIdxRef   = useRef(activeIdx);
  const isFocusedRef   = useRef(isFocused);

  /* ── 섹션 높이 측정 (슬롯 변화 시 재측정) ── */
  useEffect(() => {
    const measure = () => setHeights(sectionRefs.current.map(r => r?.offsetHeight ?? 0));
    measure();
    const obs = new ResizeObserver(measure);
    sectionRefs.current.forEach(r => r && obs.observe(r));
    return () => obs.disconnect();
  }, [slots.length]);

  /* ── 방문 상태 저장 + query 초기화 ── */
  useEffect(() => {
    sessionStorage.setItem('homeVisited', 'true');
    setQuery('');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 새 AI 대화 신호 수신 ──
     호스트에 react-router가 없어 Sidebar가 location.state 대신 navNewAiChat 이벤트로 chatId를 넘긴다.
     이벤트를 토큰(증가 카운터)으로 바꿔 담아, 아래 리셋 효과는 원본의 구조를 그대로 유지한다. */
  const [newChatSignal, setNewChatSignal] = useState<{ token: number; chatId: string | null } | null>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<{ chatId?: string }>).detail;
      setNewChatSignal(prev => ({ token: (prev?.token ?? 0) + 1, chatId: detail?.chatId ?? null }));
    };
    window.addEventListener('navNewAiChat', handler);
    return () => window.removeEventListener('navNewAiChat', handler);
  }, []);

  /* ── 새 AI 대화 버튼 → 현재 세션 저장 + 화면 리셋 ── */
  const prevNewChat = useRef<number | undefined>(undefined);
  useEffect(() => {
    const token = newChatSignal?.token;
    const incomingChatId = newChatSignal?.chatId ?? null;
    if (!token || token === prevNewChat.current) return;
    prevNewChat.current = token;

    /* 홈 프롬프트/ProActive 첫 진입 시 slots 효과가 TopNav 탭 자동 생성을 위해
       쏘는 navNewAiChat은 이미 activeChatIdRef에 반영된 chatId다. 이를 새 대화
       리셋으로 처리하면 방금 시작한 응답이 즉시 idle로 초기화되므로 무시한다.
       (원본 POC는 리셋 신호가 location.state.newChat이라 이 충돌이 없었다) */
    if (incomingChatId && incomingChatId === activeChatIdRef.current) return;

    // 현재 세션 저장
    if (activeChatIdRef.current && sessionSnapshotRef.current) {
      const snap = sessionSnapshotRef.current;
      chatSessionsRef.current.set(activeChatIdRef.current, {
        ...snap,
        slots: normalizeSlotsForSave(snap.slots),
      });
    }

    // 새 chatId 설정 (Sidebar에서 넘겨준 chatId)
    activeChatIdRef.current = incomingChatId;
    setActiveChatId(incomingChatId);

    // 새 대화 = 새 RAG 컨텍스트 — 이전 탭의 conversationId를 이어 쓰면
    // 서버가 이전 대화의 멀티턴 맥락으로 응답한다
    conversationIdRef.current = newConversationId();

    clearSlotFlowTimers();
    cancelResponse();
    setConvPhase('idle');
    setSlots([]);
    setProActiveFromHome(false);
    setSection0Collapsed(false);
    isFirstSend.current = true;
    setNewQuery('');
    setQuery('');
    // 첨부는 대화에 종속(색인이 대화 범위) — 새 대화로 넘어가면 따라가지 않는다
    setAttachments([]);
    setAttachNotice(null);

    sessionStorage.removeItem('homeVisited');
    sessionStorage.removeItem('homeActiveIdx');
    sessionStorage.removeItem('homeConvPhase');
    sessionStorage.removeItem('homeBubblePhase');
    sessionStorage.removeItem('homeBubbleText');
    sessionStorage.removeItem('homeGreetingCollapsed');
    sessionStorage.removeItem('chatState');
    sessionStorage.removeItem('chatSubmittedQuery');
    skipAnim.current = false;
    setIsConvMode(false);
    setActiveIdx(0);
    setBusy(false);
    setGreetingDone(false);
    setEntered(false);
    setTimeout(() => setEntered(true), 120);
    // 헤더 단계 시퀀서 리셋
    initStepStartedRef.current = false;
    setInitHdrTitle(null);
    setInitHdrStepIdx(0);
    setInitHdrDoneGreen(false);
    slotStepStartedRef.current.clear();
  }, [newChatSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── activeIdx 저장 ── */
  useEffect(() => { sessionStorage.setItem('homeActiveIdx', String(activeIdx)); }, [activeIdx]);

  /* ── 대화 state sessionStorage 동기화 ── */
  useEffect(() => { sessionStorage.setItem('homeConvPhase', convPhase); }, [convPhase]);
  useEffect(() => { sessionStorage.setItem('homeBubblePhase', bubblePhase); }, [bubblePhase]);
  useEffect(() => { sessionStorage.setItem('homeBubbleText', bubbleText); }, [bubbleText]);
  useEffect(() => { sessionStorage.setItem('homeGreetingCollapsed', String(greetingCollapsed)); }, [greetingCollapsed]);

  /* ── 최초 진입 트리거 ── */
  useEffect(() => {
    if (skipAnim.current) return;
    const t = setTimeout(() => setEntered(true), 120);
    return () => clearTimeout(t);
  }, []);

  /* ── greetingDone → PA 섹션 등장 + 프롬프트 포커스 ── */
  useEffect(() => {
    if (!entered || skipAnim.current) return;
    const timers: ReturnType<typeof setTimeout>[] = [];
    timers.push(setTimeout(() => setGreetingDone(true), 1900));
    return () => timers.forEach(clearTimeout);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entered]);

  /* ── 동적 섹션 수 ── */
  // ProActive 제안 섹션은 항상 마지막에 고정
  const sectionCount  = slots.length + 2; // 홈 + 대화슬롯들 + ProActive
  const sectionLabels = [
    '홈',
    ...slots.map((_, i) => `대화 ${i + 1}`),
    'ProActive 제안',
  ];

  /* sectionCount 항상 최신값 유지 (goTo에서 참조) */
  useEffect(() => { sectionCountRef.current = sectionCount; }, [sectionCount]);

  /* ── 섹션 이동 ── */
  const goTo = useCallback((next: number) => {
    if (busy) return;
    const minIdx = section0Collapsed ? 1 : 0;
    next = Math.max(minIdx, Math.min(sectionCountRef.current - 1, next));
    if (next === activeIdx) return;
    setBusy(true);
    setActiveIdx(next);
    // 일반 스크롤 모드 — scrollIntoView로 해당 섹션으로 이동
    const targetSection = sectionRefs.current[next];
    if (targetSection) {
      targetSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setTimeout(() => setBusy(false), DURATION);
  }, [activeIdx, busy, section0Collapsed]);

  /* ref 동기화 — 항상 최신값 유지 */
  useEffect(() => { goToRef.current    = goTo;     }, [goTo]);
  useEffect(() => { activeIdxRef.current = activeIdx; }, [activeIdx]);
  useEffect(() => { isFocusedRef.current = isFocused; }, [isFocused]);
  useEffect(() => { isConvModeRef.current = isConvMode; }, [isConvMode]);

  /* 세션 스냅샷 ref — 렌더마다 최신 상태 동기화 (이벤트 핸들러에서 stale closure 방지) */
  const sessionSnapshotRef = useRef<ChatSession | null>(null);
  sessionSnapshotRef.current = {
    slots,
    convPhase,
    isConvMode,
    section0Collapsed,
    greetingCollapsed,
    proActiveFromHome,
    currentProActiveCards,
    conversationId: conversationIdRef.current,
    attachments,
  };

  /* ── AI 탭 전환: 현재 세션 저장 → 새 세션 복원 ── */
  useEffect(() => {
    const handler = (e: Event) => {
      const { newChatId, prevChatId } = (e as CustomEvent<{ newChatId: string; prevChatId: string | null }>).detail;

      // 이미 활성 중인 채팅으로 "재진입"하는 경우 (예: 다른 모듈 탭에 갔다가
      // 같은 AI 대화 탭을 다시 클릭) — Home은 KeepAlive로 언마운트되지 않으므로
      // 현재 화면의 slots/convPhase가 이미 그 채팅의 최신 상태다. 여기서 저장·복원을
      // 수행하면 자칫 오래된 스냅샷으로 방금까지 보이던 최신 응답을 덮어써 버릴 수
      // 있으므로 아무 것도 하지 않고 그대로 둔다.
      if (newChatId && newChatId === activeChatIdRef.current) return;

      // 진행 중이던 응답 연출 타이머 취소 — 다른 세션 복원 후 오발화 방지
      clearSlotFlowTimers();

      // 현재 세션 저장 (streaming → done 상태로 정규화해서 저장)
      if (prevChatId && sessionSnapshotRef.current) {
        const snap = sessionSnapshotRef.current;
        chatSessionsRef.current.set(prevChatId, {
          ...snap,
          slots: normalizeSlotsForSave(snap.slots),
        });
      }

      // 새 세션 복원
      const saved = chatSessionsRef.current.get(newChatId);
      if (saved) {
        setSlots(saved.slots);
        setConvPhase(saved.convPhase);
        setIsConvMode(saved.isConvMode);
        setSection0Collapsed(saved.section0Collapsed);
        setGreetingCollapsed(saved.greetingCollapsed);
        setProActiveFromHome(saved.proActiveFromHome);
        currentProActiveCardsRef.current = saved.currentProActiveCards;
        setCurrentProActiveCards(saved.currentProActiveCards);
        // 이 탭의 RAG 대화 컨텍스트 복원 — 이후 질의·파일 색인이 탭의 멀티턴 맥락에 이어진다
        conversationIdRef.current = saved.conversationId;
        // 첨부도 함께 복원 — 색인이 이 대화에 남아 있으므로 배지도 그대로 있어야 한다
        setAttachments(saved.attachments ?? []);
        setAttachNotice(null);
      } else {
        // 새 탭 → 홈 화면으로 리셋
        setSlots([]);
        setConvPhase('idle');
        setIsConvMode(false);
        setSection0Collapsed(false);
        setGreetingCollapsed(false);
        setProActiveFromHome(false);
        const initial = fetchedProActiveCardsRef.current.length > 0
          ? fetchedProActiveCardsRef.current.slice()
          : INITIAL_PROACTIVE_CARDS.slice();
        currentProActiveCardsRef.current = initial;
        setCurrentProActiveCards(initial);
        // RAG 대화 컨텍스트도 새로 시작
        conversationIdRef.current = newConversationId();
        setAttachments([]);
        setAttachNotice(null);
      }
      setActiveChatId(newChatId);
      activeChatIdRef.current = newChatId;
    };
    window.addEventListener('navSwitchAiChat', handler as EventListener);
    return () => window.removeEventListener('navSwitchAiChat', handler as EventListener);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 홈 탭 클릭 → 현재 AI 세션 저장 + idle 홈 화면으로 복귀 ── */
  useEffect(() => {
    const handler = () => {
      // 현재 대화 세션 저장
      if (activeChatIdRef.current && sessionSnapshotRef.current) {
        const snap = sessionSnapshotRef.current;
        chatSessionsRef.current.set(activeChatIdRef.current, {
          ...snap,
          slots: normalizeSlotsForSave(snap.slots),
        });
      }
      // idle 홈 화면으로 리셋 (currentProActiveCards는 유지)
      setSlots([]);
      setConvPhase('idle');
      setIsConvMode(false);
      setSection0Collapsed(false);
      setGreetingCollapsed(false);
      setProActiveFromHome(false);
      setMouPanelOpen(false);
      setMailSidePanelOpen(false);
      setDocPreviewOpen(false);
      setAttachmentPreview(null);
      setReportPanelOpen(false);
      setAiPaDrawerOpen(false);
      mouPanelTimersRef.current.forEach(clearTimeout);
      mouPanelTimersRef.current = [];
      mouAnimationStartedRef.current = false;
      reportPanelTimersRef.current.forEach(clearTimeout);
      reportPanelTimersRef.current = [];
      reportAnimationStartedRef.current = false;
      clearSlotFlowTimers();
      activeChatIdRef.current = null;
      setActiveChatId(null);
      // RAG 대화 컨텍스트도 새로 시작
      conversationIdRef.current = newConversationId();
      setAttachments([]);
      setAttachNotice(null);
    };
    window.addEventListener('navGoHome', handler);
    return () => window.removeEventListener('navGoHome', handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 파일 첨부 드롭다운 외부 클릭 닫기 ──
     React 16은 합성 이벤트를 document에 위임하므로 attachWrap의 stopPropagation()이
     이 네이티브 리스너를 막지 못한다(같은 클릭에 열기→닫기가 연달아 실행돼 무반응).
     첨부 UI 내부(data-attach-ui) 클릭은 여기서 직접 걸러낸다. */
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Element | null;
      if (target && target.closest && target.closest('[data-attach-ui]')) return;
      setAttachDropdownOpen(null);
    };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, []);

  /* ── 스크롤 감지: scrollHintDown 버튼 show/hide + activeIdx 탑 복원 ── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const handleScroll = () => {
      const top = el.scrollTop;
      setScrolledDown(top > 60);
      // 수동 스크롤로 탑 복귀 시 activeIdx 0으로 복원 → 버튼/peekVeil 다시 표시
      if (top < 80 && activeIdxRef.current !== 0) {
        setActiveIdx(0);
      }
    };
    el.addEventListener('scroll', handleScroll, { passive: true });
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  /* ── greetingCollapsed — streaming 시점에 전환, idle 시점에 리셋 ── */
  useEffect(() => {
    if (convPhase === 'streaming' || convPhase === 'done') { setGreetingCollapsed(true); setIsConvMode(true); }
    if (convPhase === 'idle') {
      setGreetingCollapsed(false);
      setProActiveFromHome(false);
      setSection0Collapsed(false);
      setIsConvMode(false);
      prevDoneCountRef.current = 0;
      mouPanelTimersRef.current.forEach(clearTimeout);
      mouPanelTimersRef.current = [];
      mouAnimationStartedRef.current = false;
      setMouPanelOpen(false);
      setMouPanelStep(0);
      setMouDocFull(false);
      setMouGenTitle('문서 캔버스를 준비하고 있습니다');
      setMouGenDesc('ONECHAMBER 참고자료와 메일 요청사항을 검토한 뒤, 적합한 MOU 구조를 구성합니다.');
      setMouGenStatus('참고자료 분석 중');
      setMouGenFadeOut(false);
    }
  }, [convPhase]);

  /* ── convPhase 헤더 단계 시퀀서 ── */
  useEffect(() => {
    if (convPhase !== 'streaming') return;
    if (initStepStartedRef.current) return;
    initStepStartedRef.current = true;
    const STEPS = [
      { label: '요청 내용을 확인하고 있어요' },
      { label: 'ERP 회계에서 거래내역을 조회하고 있어요' },
      { label: '미수금 거래처를 선별·분석하고 있어요' },
      { label: 'ONEFFICE 보고서 초안을 작성하고 있어요' },
    ];
    setInitHdrStepTotal(STEPS.length);
    const STEP_DUR = 480;
    const INIT_DELAY = 150;
    const timers: ReturnType<typeof setTimeout>[] = [];
    STEPS.forEach((step, i) => {
      timers.push(setTimeout(() => {
        setInitHdrTitle(step.label);
        setInitHdrStepIdx(i);
      }, INIT_DELAY + i * STEP_DUR));
    });
    timers.push(setTimeout(() => setInitHdrDoneGreen(true), INIT_DELAY + STEPS.length * STEP_DUR));
    return () => timers.forEach(clearTimeout);
  }, [convPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 슬롯 헤더 단계 시퀀서 (연출용 — 라이브 슬롯은 실제 SSE step 이벤트로 헤더 갱신) ── */
  useEffect(() => {
    slots.forEach((slot) => {
      if (slot.phase !== 'streaming') return;
      if (slot.live) return;
      if (slotStepStartedRef.current.has(slot.id)) return;
      const resData = SLOT_RESPONSE_DATA[slot.responseKey] ?? SLOT_RESPONSE_DATA.custom;
      const steps = resData.steps;
      if (!steps || !steps.length) return;
      slotStepStartedRef.current.add(slot.id);
      const STEP_DUR = resData.stepDur ?? 960;
      const INIT_DELAY = 200;
      steps.forEach((step, i) => {
        setTimeout(() => {
          setSlots(prev => prev.map(s =>
            s.id === slot.id
              ? { ...s, headerTitle: step.label, headerStepIdx: i, headerStepTotal: steps.length }
              : s
          ));
        }, INIT_DELAY + i * STEP_DUR);
      });
      setTimeout(() => {
        setSlots(prev => prev.map(s =>
          s.id === slot.id ? { ...s, headerDoneGreen: true } : s
        ));
      }, INIT_DELAY + steps.length * STEP_DUR);
    });
  }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 슬롯이 streaming/done → 대화 모드 전환 + AI 탭 자동 생성 ── */
  useEffect(() => {
    if (slots.some(s => s.phase === 'streaming' || s.phase === 'done')) {
      setIsConvMode(true);
      setSection0Collapsed(true);
      // 아직 활성 AI 탭이 없으면 자동 생성 (ProActive 카드 또는 프롬프트 첫 진입)
      if (!activeChatIdRef.current) {
        const chatId = `ai-auto-${Date.now()}`;
        activeChatIdRef.current = chatId;
        setActiveChatId(chatId);
        window.dispatchEvent(new CustomEvent('navNewAiChat', { detail: { chatId } }));
      }
    }
  }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── mou-draft 슬롯 streaming 진입 → 우측 MOU 패널 오픈 + 애니메이션 ── */
  useEffect(() => {
    const hasMouDraft = slots.some(s => s.responseKey === 'mou-draft');
    const hasMouDraftActive = slots.some(
      s => s.responseKey === 'mou-draft' && (s.phase === 'streaming' || s.phase === 'done')
    );

    // mou-draft 슬롯이 완전히 사라지면 패널 초기화
    if (!hasMouDraft) {
      mouPanelTimersRef.current.forEach(clearTimeout);
      mouPanelTimersRef.current = [];
      mouAnimationStartedRef.current = false;
      setMouPanelOpen(false);
      setMouPanelStep(0);
      setMouDocFull(false);
      setMouGenTitle('문서 캔버스를 준비하고 있습니다');
      setMouGenDesc('ONECHAMBER 참고자료와 메일 요청사항을 검토한 뒤, 적합한 MOU 구조를 구성합니다.');
      setMouGenStatus('참고자료 분석 중');
      setMouGenFadeOut(false);
      return;
    }

    // 이미 애니메이션 시작했으면 중복 실행 방지 (slots가 streaming→done 전환 시 재실행됨)
    if (!hasMouDraftActive || mouAnimationStartedRef.current) return;

    mouAnimationStartedRef.current = true;
    setMailSidePanelOpen(false);
    setDocPreviewOpen(false);
    setAttachmentPreview(null);
    setMouPanelOpen(true);
    setMouPanelStep(1);
    setMouDocFull(false);
    setMouGenFadeOut(false);
    setMouGenTitle('문서 캔버스를 준비하고 있습니다');
    setMouGenDesc('ONECHAMBER 참고자료와 메일 요청사항을 검토한 뒤, 적합한 MOU 구조를 구성합니다.');
    setMouGenStatus('참고자료 분석 중');

    // 타이머는 slots 변경 시 cleanup되지 않도록 ref에만 저장
    const t1 = setTimeout(() => {
      setMouPanelStep(2);
      setMouGenTitle('문서 구조를 설계하고 있습니다');
      setMouGenDesc('표준 MOU 양식과 일본 시장 협력 시나리오를 비교해 조항 순서를 정리합니다.');
      setMouGenStatus('목차·조항 구조 설계 중');
    }, 1200);
    const t2 = setTimeout(() => {
      setMouPanelStep(3);
      setMouGenTitle('초안 문장을 생성하고 있습니다');
      setMouGenDesc('각 조항별로 역할, PoC 추진 방식, 데이터 주권 요건을 문서 문장으로 변환합니다.');
      setMouGenStatus('조항별 초안 작성 중');
    }, 2600);
    const t3 = setTimeout(() => {
      setMouPanelStep(4);
      setMouGenTitle('검토 포인트를 표시하고 있습니다');
      setMouGenDesc('법무 검토가 필요한 조항과 회신 메일에 포함할 후속 제안을 함께 정리합니다.');
      setMouGenStatus('검토 체크포인트 표시 중');
    }, 4200);
    const t4 = setTimeout(() => {
      setMouGenFadeOut(true);
      setTimeout(() => setMouDocFull(true), 260);
    }, 5800);
    mouPanelTimersRef.current = [t1, t2, t3, t4];
    // cleanup 없음 — slots 변경마다 타이머가 취소되는 버그 방지
  }, [slots]);

  /* ── MOU 문서 완성 시 reveal 애니메이션 트리거 ── */
  useEffect(() => {
    if (mouDocFull) {
      setMouDocReveal(false);
      const t = setTimeout(() => setMouDocReveal(true), 30);
      return () => clearTimeout(t);
    } else {
      setMouDocReveal(false);
    }
  }, [mouDocFull]);

  /* ── 대화 모드 자동 스크롤 ── */
  useEffect(() => {
    if (!isConvMode || !convScrollRef.current) {
      if (!isConvMode) prevDoneCountRef.current = 0; // 대화 모드 종료 시 초기화
      return;
    }
    const el = convScrollRef.current;

    // 첫 슬롯 streaming 진입 → 즉시 top
    if (slots.length === 1 && slots[0].phase === 'streaming') {
      el.scrollTop = 0;
      return;
    }

    // done 슬롯이 새로 생겼을 때만 스크롤 (idle 추가·bubblePhase 변경 등 재실행 방지)
    const doneCount = slots.filter(s => s.phase === 'done').length;
    if (doneCount <= prevDoneCountRef.current) return;
    prevDoneCountRef.current = doneCount;

    const t = setTimeout(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }, 480); // 후속 제안 카드 애니메이션(300ms) 완료 후
    return () => clearTimeout(t);
  }, [slots, isConvMode]);

  /* ── 말풍선 페이즈 드라이버 (Section 0) ── */
  useEffect(() => {
    if (convPhase === 'done') {
      if (sessionStorage.getItem('homeBubblePhase') === 'complete') return;
      setBubblePhase('loading');
      setBubbleText('');
      const t = setTimeout(() => {
        setBubblePhase('typing');
        let i = 0;
        bubbleTyperRef.current = setInterval(() => {
          i++;
          setBubbleText(BUBBLE_TEXT.slice(0, i));
          if (i >= BUBBLE_TEXT.length) {
            if (bubbleTyperRef.current) clearInterval(bubbleTyperRef.current);
            bubbleTyperRef.current = null;
            setBubblePhase('complete');
          }
        }, 36);
      }, 1200);
      return () => {
        clearTimeout(t);
        if (bubbleTyperRef.current) clearInterval(bubbleTyperRef.current);
      };
    }
    if (convPhase === 'idle') {
      setBubblePhase('hidden');
      setBubbleText('');
      if (bubbleTyperRef.current) clearInterval(bubbleTyperRef.current);
    }
  }, [convPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── erpDone 타이머 — streaming 시작 후 1200ms ── */
  useEffect(() => {
    if (convPhase === 'streaming') {
      setErpDone(false);
      const t = setTimeout(() => setErpDone(true), 1200);
      return () => clearTimeout(t);
    }
    if (convPhase === 'idle') setErpDone(false);
  }, [convPhase]);

  /* ── 대화 전환 페이즈 드라이버 ── */
  useEffect(() => {
    if (chatState === 'default') {
      if (isRestoredSession.current) {
        isRestoredSession.current = false;
        return;
      }
      setConvPhase('idle');
      isFirstSend.current = true;
      return;
    }
    if (chatState !== 'responding') return;

    if (isRestoredSession.current) {
      isRestoredSession.current = false;
      return;
    }

    if (isFirstSend.current) {
      isFirstSend.current = false;
      setConvPhase('streaming');
      const t1 = setTimeout(() => setConvPhase('done'), 2280);
      const t2 = setTimeout(() => goToRef.current(0),   2280);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    } else {
      goToRef.current(0);
      setConvPhase('streaming');
      const t1 = setTimeout(() => setConvPhase('done'), 2200);
      const t2 = setTimeout(() => goToRef.current(0),   2200);
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
  }, [chatState]);

  /* ── convPhase done → idle 슬롯 생성 + section 1로 이동 ── */
  useEffect(() => {
    if (convPhase === 'done' && slots.length === 0) {
      setSlots([{
        id: Date.now(),
        phase: 'idle',
        prompt: '',
        responseKey: '',
        erpDone: false,
        bubblePhase: 'hidden',
        bubbleText: '',
        proActiveCards: [],
        slotQuery: '',
        slotQueryFocused: false,
      }]);
      // 자동 이동 없음 — 말풍선 클릭 또는 스크롤로 이동
    }
    if (convPhase === 'idle') {
      setSlots([]);
      // ProActive 카드 목록·큐 포인터 초기화 (후속 제안 반복 클릭 후 복귀 시 카드 증식 방지)
      const initial = fetchedProActiveCardsRef.current.slice();
      currentProActiveCardsRef.current = initial.length > 0 ? initial : INITIAL_PROACTIVE_CARDS.slice();
      queuePointerRef.current = PROACTIVE_CARD_QUEUE.length;
      setCurrentProActiveCards(currentProActiveCardsRef.current);
    }
  }, [convPhase]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 슬롯 말풍선 시작 ── */
  /* ── 섹션 내 특정 엘리먼트까지 부드럽게 스크롤 (easeInOut, 700ms) ── */
  const smoothScrollToEl = (section: HTMLDivElement, el: HTMLElement, duration = 700) => {
    const elBottom = el.offsetTop + el.offsetHeight;
    const target   = Math.min(
      elBottom - section.clientHeight + 32, // 하단 32px 여백
      section.scrollHeight - section.clientHeight,
    );
    if (target <= section.scrollTop) return; // 이미 보임
    const start     = section.scrollTop;
    const distance  = target - start;
    const startTime = performance.now();
    const ease = (t: number) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    const step = (now: number) => {
      const p = Math.min((now - startTime) / duration, 1);
      section.scrollTop = start + distance * ease(p);
      if (p < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  /* 전표 전송 완료 AI 메시지가 마운트된 뒤 화면에 보이도록 스크롤 */
  const scrollToJournalSentMsg = (slotId: number) => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      journalSentMsgRefs.current.get(slotId)?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }));
  };

  const startSlotBubble = useCallback((slotIdx: number, text: string) => {
    // cleanup previous timers for this slot
    const prev = slotBubbleRefs.current.get(slotIdx);
    if (prev) {
      if (prev.t1) clearTimeout(prev.t1);
      if (prev.interval) clearInterval(prev.interval);
      if (prev.tIdle) clearTimeout(prev.tIdle);
    }

    setSlots(prev => prev.map((s, i) =>
      i === slotIdx ? { ...s, bubblePhase: 'loading', bubbleText: '' } : s
    ));

    // bubble이 DOM에 마운트된 뒤 해당 섹션 스크롤
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const bubbleEl  = slotBubbleRowRefs.current.get(slotIdx);
      const sectionEl = sectionRefs.current[1 + slotIdx];
      if (bubbleEl && sectionEl) smoothScrollToEl(sectionEl, bubbleEl);
    }));

    const t1 = setTimeout(() => {
      setSlots(prev => prev.map((s, i) =>
        i === slotIdx ? { ...s, bubblePhase: 'typing' } : s
      ));
      let charIdx = 0;
      const interval = setInterval(() => {
        charIdx++;
        const slice = text.slice(0, charIdx);
        setSlots(prev => prev.map((s, i) =>
          i === slotIdx ? { ...s, bubbleText: slice } : s
        ));
        if (charIdx >= text.length) {
          clearInterval(interval);
          slotBubbleRefs.current.set(slotIdx, { t1: null, interval: null, tIdle: null });
          setSlots(prev => prev.map((s, i) =>
            i === slotIdx ? { ...s, bubblePhase: 'complete' } : s
          ));
        }
      }, 36);
      slotBubbleRefs.current.set(slotIdx, { t1: null, interval, tIdle: null });
    }, 1200);

    slotBubbleRefs.current.set(slotIdx, { t1, interval: null, tIdle: null });
  }, []);

  /* ── ProActive 마지막 섹션에서 카드 클릭 ── */
  const handleProActiveCardClick = useCallback((card: ProActiveCardDef) => {
    const base = SLOT_RESPONSE_DATA[card.id] ?? SLOT_RESPONSE_DATA.custom;
    const responseData = card.id === 'custom' || !(card.id in SLOT_RESPONSE_DATA)
      ? { ...base, nextProActiveCards: fetchedProActiveCardsRef.current }
      : base;

    /* 클릭된 카드 제거 + 큐의 다음 카드로 자동 보충
       — ProActive 목록에 없는 카드(후속 제안 등) 클릭 시에는 목록 변경 없이 스킵
       — StrictMode에서 setState updater가 2회 호출되어 ref 부작용이 중복되므로
         ref 미러(currentProActiveCardsRef)를 읽어 setState 밖에서 계산 */
    const isInProActiveList = currentProActiveCardsRef.current.some(c => c.id === card.id);
    if (isInProActiveList) {
      const filtered = currentProActiveCardsRef.current.filter(c => c.id !== card.id);
      const displayedIds = new Set(filtered.map(c => c.id));
      let next: ProActiveCardDef | undefined;
      let ptr = queuePointerRef.current;
      while (ptr < PROACTIVE_CARD_QUEUE.length) {
        const candidate = PROACTIVE_CARD_QUEUE[ptr];
        ptr++;
        if (!displayedIds.has(candidate.id)) {
          next = candidate;
          break;
        }
      }
      queuePointerRef.current = ptr;
      const newCards = next ? [...filtered, next] : filtered;
      currentProActiveCardsRef.current = newCards;
      setCurrentProActiveCards(newCards);
    }

    const executeClick = () => {
      // 기존 idle 슬롯 제거 후 새 streaming 슬롯 추가
      // ※ setTimeout을 updater 밖에 두어 React StrictMode 이중 호출 방지
      const newSlot: ConvSlot = {
        id: Date.now(),
        phase: 'streaming',
        prompt: card.prompt,
        responseKey: card.id,
        erpDone: false,
        bubblePhase: 'hidden',
        bubbleText: '',
        proActiveCards: [],
        slotQuery: '',
        slotQueryFocused: false,
      };

      // 현재 slots에서 idle 제거하여 newSlotIdx 계산
      const nonIdleSlots = slots.filter(s => s.phase !== 'idle');
      const newSlotIdx = nonIdleSlots.length;

      setSlots([...nonIdleSlots, newSlot]);

      // conv 모드: 새 슬롯(사용자 프롬프트 + 스트리밍 카드)이 보이도록 즉시 스크롤
      if (isConvModeRef.current && convScrollRef.current) {
        slotFlowTimersRef.current.push(setTimeout(() => {
          convScrollRef.current?.scrollTo({ top: convScrollRef.current.scrollHeight, behavior: 'smooth' });
        }, 60));
      }

      // 새 슬롯 섹션으로 이동 (섹션 모드)
      slotFlowTimersRef.current.push(setTimeout(() => goToRef.current(1 + newSlotIdx), 50));

      // 1200ms 후 erpDone
      slotFlowTimersRef.current.push(setTimeout(() => {
        setSlots(s => s.map((sl, i) =>
          i === newSlotIdx ? { ...sl, erpDone: true } : sl
        ));
      }, 1200));

      // 3200ms 후 done + 새 idle 슬롯 추가
      slotFlowTimersRef.current.push(setTimeout(() => {
        setSlots(s => {
          // 홈 복귀/세션 전환으로 슬롯이 사라진 뒤 오발화된 경우 무시
          if (!s.some(sl => sl.id === newSlot.id)) return s;
          const updated = s.map((sl, i) =>
            i === newSlotIdx ? { ...sl, phase: 'done' as SlotPhase } : sl
          );
          const newIdleSlot: ConvSlot = {
            id: Date.now() + 1,
            phase: 'idle',
            prompt: '',
            responseKey: '',
            erpDone: false,
            bubblePhase: 'hidden',
            bubbleText: '',
            proActiveCards: [],
            slotQuery: '',
            slotQueryFocused: false,
          };
          return [...updated, newIdleSlot];
        });
        slotFlowTimersRef.current.push(setTimeout(() => startSlotBubble(newSlotIdx, responseData.bubbleText), 2000));
      }, 3200));
    };

    // 홈 화면(idle)이고 아직 대화 모드 미진입 시: 인사말·프롬프트 페이드아웃 애니메이션 먼저 진행
    if (convPhase === 'idle' && !isConvMode) {
      setProActiveFromHome(true);          // 인사말·프롬프트 fade-out 시작
      slotFlowTimersRef.current.push(setTimeout(() => {
        setSection0Collapsed(true);
        setHeights(h => h.length > 0 ? [0, ...h.slice(1)] : h); // heights[0] 즉시 0으로 (ResizeObserver 딜레이 없이)
        executeClick();
      }, 350));
    } else {
      executeClick();
    }
  }, [slots, startSlotBubble, convPhase, isConvMode]);

  /* ── 메인 검색창 → 실제 RAG API 스트리밍 슬롯 생성 ── */
  const startLiveSlot = useCallback((q: string) => {
    const slotId = Date.now();
    const newSlot: ConvSlot = {
      id: slotId,
      phase: 'streaming',
      prompt: q,
      responseKey: 'live',
      live: true,
      liveText: '',
      erpDone: false,
      bubblePhase: 'hidden',
      bubbleText: '',
      proActiveCards: [],
      slotQuery: '',
      slotQueryFocused: false,
    };

    const nonIdleSlots = slots.filter(s => s.phase !== 'idle');
    const newSlotIdx = nonIdleSlots.length;
    setSlots([...nonIdleSlots, newSlot]);

    if (isConvModeRef.current && convScrollRef.current) {
      slotFlowTimersRef.current.push(setTimeout(() => {
        convScrollRef.current?.scrollTo({ top: convScrollRef.current.scrollHeight, behavior: 'smooth' });
      }, 60));
    }
    slotFlowTimersRef.current.push(setTimeout(() => goToRef.current(1 + newSlotIdx), 50));

    const patchSlot = (patch: (s: ConvSlot) => Partial<ConvSlot>) =>
      setSlots(prev => prev.map(s => (s.id === slotId ? { ...s, ...patch(s) } : s)));

    /* 스트림 종료 → done 전환 + 후속 입력용 idle 슬롯 추가 (홈 복귀로 슬롯이 사라졌으면 무시) */
    const finalize = () => setSlots(prev => {
      const target = prev.find(s => s.id === slotId);
      if (!target || target.phase === 'done') return prev;
      const updated = prev.map(s =>
        s.id === slotId ? { ...s, phase: 'done' as SlotPhase, erpDone: true, headerDoneGreen: true } : s
      );
      const newIdleSlot: ConvSlot = {
        id: Date.now() + 1,
        phase: 'idle',
        prompt: '',
        responseKey: '',
        erpDone: false,
        bubblePhase: 'hidden',
        bubbleText: '',
        proActiveCards: [],
        slotQuery: '',
        slotQueryFocused: false,
      };
      return [...updated, newIdleSlot];
    });

    // 서버 대화 메모리에는 텍스트만 남는다 — 활성 이미지는 매 턴 다시 싣는다
    // 이미지 원본은 몇 턴까지만 싣는다 — 이후에는 색인된 전사 텍스트가 검색으로 답한다
    const turnImages = attachmentsRef.current
      .filter(a => a.image && a.image.turns < IMAGE_TURN_LIMIT)
      .map(a => ({ mimeType: a.image!.mimeType, data: a.image!.data }));
    if (turnImages.length > 0) consumeImageTurn();

    streamChat({
      conversationId: conversationIdRef.current,
      message: q,
      images: turnImages,
      onStep: step => patchSlot(() => ({ liveStep: step })),
      onChunk: chunk => patchSlot(s => ({ liveText: (s.liveText ?? '') + chunk })),
      onDone: finalize,
    })
      .then(finalize) // done 이벤트 미수신 대비 — reader 종료 시에도 마무리
      .catch(err => {
        patchSlot(s => ({ liveText: `${s.liveText ?? ''}\n\n오류가 발생했습니다. 잠시 후 다시 시도하세요.` }));
        finalize();
      });
  }, [slots]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 홈 프롬프트 전송 핸들러 — 메인 검색창은 실제 RAG API 호출 ── */
  const handleHomePromptSend = useCallback(() => {
    const q = query.trim();
    if (!q) return;
    exitFocused();
    setQuery('');
    /* 홈 화면(idle) 첫 진입 시: 인사말·프롬프트 페이드아웃 후 슬롯 시작 (ProActive 카드 클릭과 동일 연출) */
    if (convPhase === 'idle' && !isConvMode) {
      setProActiveFromHome(true);
      slotFlowTimersRef.current.push(setTimeout(() => {
        setSection0Collapsed(true);
        setHeights(h => h.length > 0 ? [0, ...h.slice(1)] : h);
        startLiveSlot(q);
      }, 350));
    } else {
      startLiveSlot(q);
    }
  }, [query, exitFocused, setQuery, startLiveSlot, convPhase, isConvMode]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── ProActive 카드 닫기 (dismiss: 클릭 없이 목록에서만 제거) ── */
  const handleProActiveCardDismiss = useCallback((card: ProActiveCardDef) => {
    const filtered = currentProActiveCardsRef.current.filter(c => c.id !== card.id);
    const displayedIds = new Set(filtered.map(c => c.id));
    let next: ProActiveCardDef | undefined;
    let ptr = queuePointerRef.current;
    while (ptr < PROACTIVE_CARD_QUEUE.length) {
      const candidate = PROACTIVE_CARD_QUEUE[ptr];
      ptr++;
      if (!displayedIds.has(candidate.id)) { next = candidate; break; }
    }
    queuePointerRef.current = ptr;
    const newCards = next ? [...filtered, next] : filtered;
    currentProActiveCardsRef.current = newCards;
    setCurrentProActiveCards(newCards);
  }, []);

  /* ── postSuggestCard 클릭 → ProActive와 동일한 슬롯 응답 흐름 ── */
  const handlePostSuggestClick = useCallback((item: PostSuggestItem) => {
    if (!item.prompt || !item.responseKey) return;
    handleProActiveCardClick({
      id: item.responseKey,
      title: item.title,
      metaType: 'none',
      metaText: '',
      desc: item.desc,
      actionLabel: item.actionLabel,
      actionStyle: item.actionStyle,
      prompt: item.prompt,
    });
  }, [handleProActiveCardClick]);

  /* ── 슬롯 프롬프트 입력 전송 — 메인 검색창과 동일하게 실제 RAG API 호출 ── */
  const handleSlotSend = useCallback((slotIdx: number) => {
    const slot = slots[slotIdx];
    if (!slot || !slot.slotQuery.trim()) return;
    const prompt = slot.slotQuery.trim();
    // 파일 + 입력창 초기화
    setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, slotQuery: '' } : s));
    startLiveSlot(prompt);
  }, [slots, startLiveSlot]);

  /* ── 대화 모드 플로팅 프롬프트 전송 — 메인 검색창과 동일하게 실제 RAG API 호출 ── */
  const handleConvModeSend = useCallback(() => {
    if (!newQuery.trim()) return;
    const q = newQuery.trim();
    setNewQuery('');
    startLiveSlot(q);
  }, [newQuery, startLiveSlot]);

  /* ── 휠 이벤트 — 일반 스크롤 모드에서 비활성화 ── */
  // (섹션 snap 방식 제거됨: 페이지가 overflow-y: auto로 자연 스크롤)

  /* ── 키보드 ── */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isFocusedRef.current) return;
      if (e.key === 'ArrowDown') goToRef.current(activeIdxRef.current + 1);
      if (e.key === 'ArrowUp')   goToRef.current(activeIdxRef.current - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── 트랙 스타일 (일반 스크롤 모드) ── */
  const trackStyle = (): React.CSSProperties => ({
    opacity: entered ? 1 : 0,
    transition: entered ? 'opacity 0.5s ease' : 'none',
  });

  /* ── 섹션별 스타일 (일반 스크롤 모드 — 블러/스냅 제거) ── */
  const sectionStyle = (_idx: number): React.CSSProperties => ({});

  /* ── 핸들러 ── */
  const handleCardMouseDown = (e: React.MouseEvent) => e.stopPropagation();
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      /* 한글 등 IME 조합이 아직 끝나지 않은 상태(예: '줘'를 입력한 직후, 스페이스로
         확정하기 전)의 Enter는 조합을 확정하는 키일 수 있다. 이 경우 그대로 전송해버리면
         조합 중이던 마지막 글자만 남고 앞의 텍스트가 유실된다. 조합 중에는 전송하지 않고
         IME가 조합을 확정하도록 그대로 둔다. */
      if (isComposingRef.current || e.nativeEvent.isComposing) return;
      e.preventDefault();
      /* 전송 버튼과 동일하게 handleHomePromptSend 경로를 사용한다. 과거 sendMessage()
         (ChatContext) 호출은 convPhase 기반 하드코딩 응답 카드(현재 죽은 코드,
         {false && ...})만 구독하고 있어 Enter 전송이 반영되지 않는 오작동이 있었다. */
      handleHomePromptSend();
    }
  };

  /* 대화 중인지 여부 */
  const isConversing = convPhase !== 'idle';

  /* ── 슬롯 모듈 칩 아이콘 ── */
  const SlotChipIcon = ({ iconType, done }: { iconType: 'bar' | 'doc'; done: boolean }) => {
    const color = done ? '#10B981' : '#3B82F6';
    if (iconType === 'bar') return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 20V10M12 20V4M6 20v-6"/>
      </svg>
    );
    return (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
      </svg>
    );
  };

  return (
    <div
      ref={containerRef}
      className={styles.page}
      onClick={(e) => {
        if (isFocused && cardRef.current && !cardRef.current.contains(e.target as Node)) exitFocused();
      }}
    >
      {/* ── 배경 블롭 ── */}
      <div className={styles.bgWrap} aria-hidden="true">
        <div className={`${styles.blob} ${styles.blob1}`} />
        <div className={`${styles.blob} ${styles.blob2}`} />
        <div className={`${styles.blob} ${styles.blob3}`} />
      </div>

      {!isConvMode ? (<>
      <div ref={trackRef} className={styles.track} style={trackStyle()}>

        {/* ══════════════════════════════════════════
            SECTION 0 — Hero / Conversation
        ══════════════════════════════════════════ */}
        <div
          ref={el => { sectionRefs.current[0] = el; }}
          className={`${styles.section} ${styles.sectionHero}`}
          style={{
            ...sectionStyle(0),
            ...(section0Collapsed ? { maxHeight: 0, overflow: 'hidden', padding: 0 } : {}),
          }}
        >
          <div className={styles.inner}>

            {/* 인사말 */}
            <div
              className={`${styles.greetingWrap} ${entered && convPhase === 'idle' && !proActiveFromHome ? styles.greetingFadeIn : ''} ${convPhase === 'done' || proActiveFromHome ? styles.greetingHidden : ''}`}
              style={greetingCollapsed ? { display: 'none' } : undefined}
            >
              <h1 className={styles.greetingTitle}>
                안녕하세요. <span className={styles.greetingName}>김민준</span>님
              </h1>
              <p className={`${styles.greetingSub} ${isConversing ? styles.greetingSubConv : ''}`}>
                ProActive AI가 업무 흐름을 분석했습니다.{' '}
                <span className={styles.greetingHL}>자연어로 요청</span>하거나 추천 업무를 바로 실행하세요.
              </p>
            </div>

            {/* ── 일반 상태 (idle) ── */}
            {convPhase === 'idle' && (
              <div className={proActiveFromHome ? styles.idleAreaFadeOut : undefined}>
                <div
                  ref={cardRef}
                  className={`${styles.promptCard} ${isFocused ? styles.promptCardFocused : ''}`}
                  onMouseDown={handleCardMouseDown}
                >
                  <div className={styles.promptExpanded}>
                    <AttachmentStrip attachments={attachments} notice={attachNotice} onRemove={removeAttachment} />
                    <textarea
                      ref={textareaRef}
                      className={styles.promptTextarea}
                      rows={2}
                      placeholder="무엇을 도와드릴까요?"
                      value={query}
                      onChange={e => setQuery(e.target.value)}
                      onCompositionStart={() => { isComposingRef.current = true; }}
                      onCompositionEnd={() => { isComposingRef.current = false; }}
                      onKeyDown={handleKeyDown}
                      onFocus={() => {
                        if (!isFocused) enterFocused();
                      }}
                    />
                    <div className={styles.promptToolbar}>
                      <div className={styles.toolbarLeft}>
                        <button className={styles.sparkBtn} title="AI 도구">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                            <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                          </svg>
                        </button>
                        {/* ── 파일 첨부 드롭다운 ── */}
                        <div className={styles.attachWrap} data-attach-ui onClick={e => e.stopPropagation()}>
                          <button
                            className={`${styles.promptIconBtn} ${attachDropdownOpen === 'home' ? styles.attachBtnActive : ''}`}
                            title="파일 첨부"
                            onClick={() => setAttachDropdownOpen(prev => prev === 'home' ? null : 'home')}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                            </svg>
                          </button>
                          <div className={`${styles.attachDropdown} ${attachDropdownOpen === 'home' ? styles.attachDropdownOpen : ''}`}>
                            {/* PC에서 첨부 */}
                            <button className={styles.attachOption} onClick={() => { setAttachDropdownOpen(null); homeFileInputRef.current?.click(); }}>
                              <span className={styles.attachOptionIcon}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/>
                                </svg>
                              </span>
                              <span className={styles.attachOptionText}>
                                <strong>PC에서 첨부하기</strong>
                                <span>내 컴퓨터의 파일 선택</span>
                              </span>
                            </button>
                            {/* ONECHAMBER에서 첨부 */}
                            <button className={styles.attachOption} onClick={() => {
                              setAttachDropdownOpen(null);
                              setAttachments(prev => [...prev, {
                                uid: newAttachmentUid(), name: 'ONECHAMBER_신규사업_시장조사_2024.pptx', kind: 'doc' as const,
                              }]);
                              textareaRef.current?.focus();
                            }}>
                              <span className={`${styles.attachOptionIcon} ${styles.attachOptionIconOC}`}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                                </svg>
                              </span>
                              <span className={styles.attachOptionText}>
                                <strong>ONECHAMBER에서 첨부하기</strong>
                                <span>업무 파일 검색 및 선택</span>
                              </span>
                            </button>
                          </div>
                          {/* 숨긴 파일 input */}
                          <input
                            ref={homeFileInputRef}
                            type="file"
                            hidden
                            multiple
                            onChange={e => {
                              const rawFiles = Array.from(e.target.files ?? []);
                              e.target.value = '';
                              textareaRef.current?.focus();
                              // 문서는 RAG 색인, 이미지는 채팅 요청에 직접 싣는다
                              attachFiles(rawFiles);
                            }}
                          />
                        </div>
                      </div>
                      <div className={styles.toolbarRight}>
                        <button className={styles.promptIconBtn} title="음성 입력">
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#B0BCCF" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/>
                          </svg>
                        </button>
                        <div className={styles.llmVsep} />
                        <LLMSelector dropUp={true} />
                        <button className={styles.promptSendBtn} onClick={handleHomePromptSend} title="전송">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 2L11 13"/>
                            <path d="M22 2L15 22L11 13L2 9L22 2Z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>
                </div>


              </div>
            )}

            {/* ── 대화 뷰 (streaming / done) ── */}
            {false && (
              <>
                {/* [제거됨] convPhase 기반 하드코딩 응답 카드 — ar-report 슬롯으로 대체 */}
                <div className={styles.convBubbleRow}>
                  <div className={`${styles.promptCard} ${styles.userMsgCard}`}>{submittedQuery}</div>
                </div>

                {convPhase !== 'split' && (
                  <div className={styles.responseCard}>
                    <div className={styles.responseHeader}>
                      <span className={styles.respHeaderIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#3B82F6">
                          <path d="M12 2l1.5 8.5L22 12l-8.5 1.5L12 22l-1.5-8.5L2 12l8.5-1.5z"/>
                        </svg>
                      </span>
                      {convPhase === 'streaming' ? (
                        initHdrTitle ? (
                          <>
                            <span key={initHdrStepIdx} className={`${styles.respTitle} ${styles.respTitleStep}`}>{initHdrTitle}</span>
                            <span className={styles.respStepCount}>{initHdrStepIdx + 1} / {initHdrStepTotal}</span>
                            <span className={styles.hdrSteps}>
                              {Array.from({ length: initHdrStepTotal }).map((_, k) => (
                                <span key={k} className={k < initHdrStepIdx ? styles.hdrStepDone : k === initHdrStepIdx ? styles.hdrStepActive : styles.hdrStep} />
                              ))}
                            </span>
                          </>
                        ) : (
                          <>
                            <span className={styles.respTitle}>ProActive AI 응답 생성 중...</span>
                            <div className={styles.rDots}><div className={styles.rDot}/><div className={styles.rDot}/><div className={styles.rDot}/></div>
                          </>
                        )
                      ) : (
                        <span key="init-done" className={`${styles.respTitle} ${initHdrDoneGreen ? styles.respTitleGreen : styles.respTitleDone}`}>ProActive AI 응답 완료</span>
                      )}
                    </div>

                    <div className={styles.responseBody}>
                      {convPhase === 'streaming' ? (
                        <>
                          <p className={styles.streamingText}>
                            분석 결과, 이번주 미수금이 있는 거래처는 총 3곳이에요:
                            <span className={styles.rCursor} />
                          </p>
                          <div className={styles.skeletons}>
                            <div className={styles.skelLine} style={{ width: '65%' }} />
                            <div className={styles.skelLine} style={{ width: '80%' }} />
                            <div className={styles.skelLine} style={{ width: '52%' }} />
                          </div>
                        </>
                      ) : (
                        <div className={styles.resultContent}>
                          <p className={styles.resultIntro}>
                            분석 결과, 이번주 미수금이 있는 거래처는 총 <strong>3곳</strong>이에요:
                          </p>
                          <table className={styles.resultTable}>
                            <thead>
                              <tr>
                                <th>거래처명</th>
                                <th>미수금액(JPY)</th>
                                <th>마감일</th>
                                <th>담당자</th>
                                <th>상태</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td>㈜아사히 전자</td>
                                <td className={styles.tdAmount}>2,450,000</td>
                                <td>2025.08.25</td>
                                <td>田中 部長</td>
                                <td><AXStatusBadge size="sm" label="연체" {...AR_STATUS_STYLE.stOverdue} /></td>
                              </tr>
                              <tr>
                                <td>도쿄 물산 주식회사</td>
                                <td className={styles.tdAmount}>1,820,000</td>
                                <td>2025.08.28</td>
                                <td>佐藤 씨</td>
                                <td><AXStatusBadge size="sm" label="마감 임박" {...AR_STATUS_STYLE.stWarning} /></td>
                              </tr>
                              <tr>
                                <td>오사카 상사</td>
                                <td className={styles.tdAmount}>3,100,000</td>
                                <td>2025.08.30</td>
                                <td>山田 씨</td>
                                <td><AXStatusBadge size="sm" label="처리 중" {...AR_STATUS_STYLE.stProgress} /></td>
                              </tr>
                            </tbody>
                          </table>
                          <div className={styles.resultNote}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                            <span>ONEFFICE 보고서 초안이 생성되었어요. 내용을 검토한 후 제출할 수 있어요.</span>
                            <button className={styles.openBtn} onClick={openReportPanel}>보고서 열기 →</button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.moduleChips}>
                      <div className={`${styles.moduleChip} ${erpDone ? styles.chipDone : styles.chipLoading}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={erpDone ? '#10B981' : '#3B82F6'} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M18 20V10M12 20V4M6 20v-6"/>
                        </svg>
                        <span>[ERP 회계 ▶ 거래내역]</span>
                        {erpDone ? (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                        ) : (
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M7 17L17 7M7 7h10v10"/></svg>
                        )}
                      </div>
                      <div className={`${styles.moduleChip} ${styles.chipDone}`}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        <span>[ONEFFICE ▶ 보고서 초안]</span>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                      </div>
                    </div>

                  </div>
                )}

                {/* AI가 추가로 감지한 업무 제안 */}
                {convPhase === 'done' && (
                  <div className={styles.convPostSuggestWrap}>
                    <div className={styles.postSuggestHeader}>
                      <div className={styles.postSuggestDot}/>
                      <span className={styles.postSuggestTitle}>AI가 추가로 감지한 업무 제안이에요</span>
                    </div>
                    <div className={styles.postSuggestGrid}>
                      {postResponseSuggestions.map((item, idx) => (
                        <div key={item.id} className={styles.postSuggestCardSlide} style={{ animationDelay: `${idx * 0.12}s` }}>
                          <PostSuggestCard item={item} idx={idx} onClick={item.id === 921 ? openMouReply : item.prompt ? () => handlePostSuggestClick(item) : undefined} onActionModal={setOpenActionModal} />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* 다음 액션 말풍선 */}
                {bubblePhase !== 'hidden' && (
                  <div
                    className={styles.nextActionRow}
                    onClick={() => bubblePhase === 'complete' && goTo(1)}
                    style={{ cursor: bubblePhase === 'complete' ? 'pointer' : 'default' }}
                  >
                    <div className={styles.nextActionAvatar}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                        <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                      </svg>
                    </div>
                    <div className={styles.nextActionBubble}>
                      {bubblePhase === 'loading' && (
                        <div className={styles.bubbleDots}>
                          <div className={styles.bubbleDot} />
                          <div className={styles.bubbleDot} />
                          <div className={styles.bubbleDot} />
                        </div>
                      )}
                      {(bubblePhase === 'typing' || bubblePhase === 'complete') && (
                        <>
                          <span>
                            {bubbleText}
                            {bubblePhase === 'typing' && <span className={styles.bubbleCursor} />}
                          </span>
                          {bubblePhase === 'complete' && (
                            <svg className={styles.nextActionArrow} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M12 5v14M5 12l7 7 7-7"/>
                            </svg>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

          </div>
        </div>

        {/* SECTION 1..N — 동적 대화 슬롯 (ProActive 섹션은 항상 별도 마지막 섹션) */}

        {/* ══════════════════════════════════════════
            SECTIONS 1..N — 동적 대화 슬롯
        ══════════════════════════════════════════ */}
        {slots.map((slot, slotIdx) => {
          const resData = SLOT_RESPONSE_DATA[slot.responseKey] ?? SLOT_RESPONSE_DATA.custom;
          const sectionIdx = 1 + slotIdx;
          // idle 슬롯의 경우 이전 done 슬롯에서 postSuggestCards를 가져옴
          // done 슬롯이 없으면(초기 홈 프롬프트 흐름) postResponseSuggestions를 fallback으로 사용
          const prevDoneSlot = slot.phase === 'idle' ? slots.slice(0, slotIdx).reverse().find(s => s.phase === 'done') : null;
          const idlePostCards = prevDoneSlot
            ? (SLOT_RESPONSE_DATA[prevDoneSlot.responseKey] ?? SLOT_RESPONSE_DATA.custom).postSuggestCards
            : postResponseSuggestions;

          return (
            <div
              key={slot.id}
              ref={el => { sectionRefs.current[sectionIdx] = el; }}
              className={styles.section}
              style={sectionStyle(sectionIdx)}
            >
              <div className={styles.innerProactive}>

                {/* ── IDLE: 프롬프트 입력창 + AI가 추가로 감지한 업무 카드 ── */}
                {slot.phase === 'idle' && (
                  <>
                    {/* 후속 프롬프트 레이블 — 이전 done 슬롯의 nextActionBubble 텍스트 표시 */}
                    <div className={`${styles.hintChipsLabel} ${styles.postPromptLabel}`}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="#3B82F6">
                        <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                      </svg>
                      {prevDoneSlot?.bubbleText || 'AI가 다음 업무를 제안드려요. 어떤 것부터 시작할까요?'}
                    </div>

                    {/* 새 프롬프트 입력창 */}
                    <div className={styles.newPromptWrap}>
                      <div className={`${styles.promptCard} ${slot.slotQueryFocused ? styles.promptCardFocused : ''}`}>
                        <div className={styles.promptExpanded}>
                          <AttachmentStrip attachments={attachments} notice={attachNotice} onRemove={removeAttachment} />
                          <textarea
                            className={styles.promptTextarea}
                            rows={2}
                            placeholder="말씀해 주시면 바로 처리해드리겠습니다."
                            value={slot.slotQuery}
                            onChange={e => {
                              const val = e.target.value;
                              setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, slotQuery: val } : s));
                            }}
                            onFocus={() => setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, slotQueryFocused: true } : s))}
                            onBlur={() => setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, slotQueryFocused: false } : s))}
                            onCompositionStart={() => { isComposingRef.current = true; }}
                            onCompositionEnd={() => { isComposingRef.current = false; }}
                            onKeyDown={e => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                if (isComposingRef.current || e.nativeEvent.isComposing) return;
                                e.preventDefault();
                                handleSlotSend(slotIdx);
                              }
                            }}
                          />
                          <div className={styles.promptToolbar}>
                            <div className={styles.toolbarLeft}>
                              <button className={styles.sparkBtn} title="AI 도구">
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="#fff">
                                  <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                                </svg>
                              </button>
                              {/* 슬롯 파일 첨부 드롭다운 */}
                              <div className={styles.attachWrap} data-attach-ui onClick={e => e.stopPropagation()}>
                                <button
                                  className={`${styles.promptIconBtn} ${attachDropdownOpen === slotIdx ? styles.attachBtnActive : ''}`}
                                  title="파일 첨부"
                                  onClick={() => setAttachDropdownOpen(prev => prev === slotIdx ? null : slotIdx)}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                                  </svg>
                                </button>
                                <div className={`${styles.attachDropdown} ${attachDropdownOpen === slotIdx ? styles.attachDropdownOpen : ''}`}>
                                  <button className={styles.attachOption} onClick={() => {
                                    setAttachDropdownOpen(null);
                                    const inp = document.createElement('input');
                                    inp.type = 'file'; inp.multiple = true;
                                    inp.onchange = () => {
                                      const rawFiles = Array.from(inp.files ?? []);
                                      // 문서는 RAG 색인, 이미지는 채팅 요청에 직접 싣는다
                                      attachFiles(rawFiles);
                                    };
                                    inp.click();
                                  }}>
                                    <span className={styles.attachOptionIcon}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/>
                                      </svg>
                                    </span>
                                    <span className={styles.attachOptionText}>
                                      <strong>PC에서 첨부하기</strong>
                                      <span>내 컴퓨터의 파일 선택</span>
                                    </span>
                                  </button>
                                  <button className={styles.attachOption} onClick={() => {
                                    setAttachDropdownOpen(null);
                                    setAttachments(prev => [...prev, {
                                      uid: newAttachmentUid(), name: 'ONECHAMBER_신규사업_시장조사_2024.pptx', kind: 'doc' as const,
                                    }]);
                                  }}>
                                    <span className={`${styles.attachOptionIcon} ${styles.attachOptionIconOC}`}>
                                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                                      </svg>
                                    </span>
                                    <span className={styles.attachOptionText}>
                                      <strong>ONECHAMBER에서 첨부하기</strong>
                                      <span>업무 파일 검색 및 선택</span>
                                    </span>
                                  </button>
                                </div>
                              </div>
                            </div>
                            <div className={styles.toolbarRight}>
                              <LLMSelector dropUp={true} />
                              <button className={styles.sendBtn} onClick={() => handleSlotSend(slotIdx)}>
                                전송
                                <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M5 12h14M12 5l7 7-7 7"/>
                                </svg>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* 후속 힌트 칩 */}
                    <div className={`${styles.hintChipsWrap} ${styles.postHintChipsWrap}`}>
                      <div className={styles.hintChipsLabel}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="#7C3AED">
                          <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                        </svg>
                        AI 추가 제안 기반 메뉴 바로가기
                      </div>
                      <div className={styles.hintChipsRow}>
                        <button className={styles.chipsNavLeft} onClick={() => postChipsRef.current?.scrollBy({ left: -200, behavior: 'smooth' })} onMouseDown={e => e.preventDefault()}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
                        </button>
                        <div className={styles.hintChipsInner} ref={postChipsRef}>
                          {postHintChips.map((chip, i) => (
                            <button key={i} className={styles.hintChip} onMouseDown={e => e.preventDefault()}
                              onClick={() => setSlots(prev => prev.map((s, idx2) => idx2 === slotIdx ? { ...s, slotQuery: chip.prompt } : s))}>
                              <span className={styles.chipMod} style={{ background: chip.modBg, color: chip.modColor }}>
                                <span className={styles.chipDot} style={{ background: chip.dotColor }} />
                                {chip.mod}
                              </span>
                              <span className={styles.chipSep}>›</span>
                              <span className={styles.chipMenu}>{chip.menu}</span>
                              <span className={styles.chipArrow}>›</span>
                            </button>
                          ))}
                        </div>
                        <button className={styles.chipsNavRight} onClick={() => postChipsRef.current?.scrollBy({ left: 200, behavior: 'smooth' })} onMouseDown={e => e.preventDefault()}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
                        </button>
                      </div>
                    </div>

                    {/* AI가 추가로 감지한 업무 제안 카드 (정보형·파란색) */}
                    <div className={styles.postSuggestHeader}>
                      <div className={styles.postSuggestDot} />
                      <span className={styles.postSuggestTitle}>AI가 추가로 감지한 업무 제안이에요</span>
                    </div>
                    <div className={styles.postSuggestGrid}>
                      {idlePostCards.map((item, idx) => (
                        <PostSuggestCard
                          key={item.id}
                          item={item}
                          idx={idx}
                          onClick={item.id === 921 ? openMouReply : item.prompt ? () => handlePostSuggestClick(item) : undefined}
                          onActionModal={setOpenActionModal}
                        />
                      ))}
                    </div>
                  </>
                )}

                {/* ── STREAMING / DONE: AI 응답 뷰 ── */}
                {(slot.phase === 'streaming' || slot.phase === 'done') && (
                  <>
                    {/* 사용자 프롬프트 버블 */}
                    <div className={styles.convBubbleRow}>
                      <div className={styles.userMsgWrap}>
                        {/* 첨부 이미지 — 말풍선 위에 미리보기로 표시 */}
                        <div className={`${styles.promptCard} ${styles.userMsgCard}`}>
                          <div>{slot.prompt}</div>
                        </div>
                      </div>
                    </div>

                    {/* AI 응답 카드 */}
                    <div className={styles.responseCard}>
                      <div className={styles.responseHeader}>
                        <span className={styles.respHeaderIcon}>
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="#3B82F6">
                            <path d="M12 2l1.5 8.5L22 12l-8.5 1.5L12 22l-1.5-8.5L2 12l8.5-1.5z"/>
                          </svg>
                        </span>
                        {slot.phase === 'streaming' ? (
                          slot.headerTitle ? (
                            <>
                              <span key={slot.headerStepIdx} className={`${styles.respTitle} ${styles.respTitleStep}`}>{slot.headerTitle}</span>
                              <span className={styles.respStepCount}>{(slot.headerStepIdx ?? 0) + 1} / {slot.headerStepTotal ?? 1}</span>
                              <span className={styles.hdrSteps}>
                                {Array.from({ length: slot.headerStepTotal ?? 1 }).map((_, k) => (
                                  <span key={k} className={k < (slot.headerStepIdx ?? 0) ? styles.hdrStepDone : k === (slot.headerStepIdx ?? 0) ? styles.hdrStepActive : styles.hdrStep} />
                                ))}
                              </span>
                            </>
                          ) : (
                            <>
                              <span key={slot.live ? slot.liveStep : undefined} className={`${styles.respTitle} ${slot.live && slot.liveStep ? styles.respTitleStep : ''}`}>{(slot.live && slot.liveStep) || resData.streamingTitle || 'ProActive AI 응답 생성 중...'}</span>
                              <div className={styles.rDots}><div className={styles.rDot}/><div className={styles.rDot}/><div className={styles.rDot}/></div>
                            </>
                          )
                        ) : (
                          <span key={`done-${slot.id}`} className={`${styles.respTitle} ${slot.headerDoneGreen ? styles.respTitleGreen : styles.respTitleDone}`}>{resData.doneTitle || 'ProActive AI 응답 완료'}</span>
                        )}
                      </div>

                      <div className={styles.responseBody}>
                        {slot.live ? (
                          <LiveResponseBody slot={slot} />
                        ) : slot.phase === 'streaming' ? (
                          <>
                            <p className={styles.streamingText}>
                              {resData.streamingText}
                              <span className={styles.rCursor} />
                            </p>
                            <div className={styles.skeletons}>
                              <div className={styles.skelLine} style={{ width: '72%' }} />
                              <div className={styles.skelLine} style={{ width: '58%' }} />
                              <div className={styles.skelLine} style={{ width: '85%' }} />
                            </div>
                          </>
                        ) : (
                          <div className={styles.resultContent}>
                            {/* AI 인사이트 카드 */}
                            {resData.insightCard && (
                              <div className={styles.insightCard}>
                                <div className={styles.insightCardLabel}>{resData.insightCard.label}</div>
                                <div className={styles.insightCardTitle}>{resData.insightCard.title}</div>
                                <div className={styles.insightCardText}>{resData.insightCard.text}</div>
                              </div>
                            )}
                            {/* 메일 브리핑 카드 */}
                            {resData.briefingCard && (
                              <div className={styles.briefingCard}>
                                <div className={styles.briefingCardLabel}>{resData.briefingCard.label}</div>
                                <div className={styles.briefingCardTitle}>{resData.briefingCard.title}</div>
                                <div className={styles.briefingCardRows}>
                                  {resData.briefingCard.rows.map((row, i) => (
                                    <div key={i} className={styles.briefingCardRow}>
                                      <span className={styles.briefingCardRowKey}>{row.key}</span>
                                      <span className={styles.briefingCardRowVal}>{row.value}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <p className={styles.resultIntro}>{resData.resultText}</p>
                            {/* 거래처 미수금 테이블 (ar-report) */}
                            {resData.arTable && resData.arTable.length > 0 && (
                              <table className={styles.resultTable}>
                                <thead>
                                  <tr><th>거래처명</th><th>미수금액(JPY)</th><th>마감일</th><th>담당자</th><th>상태</th></tr>
                                </thead>
                                <tbody>
                                  {resData.arTable.map((row, ri) => (
                                    <tr key={ri}>
                                      <td>{row.name}</td>
                                      <td className={styles.tdAmount}>{row.amount}</td>
                                      <td>{row.dueDate}</td>
                                      <td>{row.person}</td>
                                      <td><AXStatusBadge size="sm" label={row.statusText} {...AR_STATUS_STYLE[row.statusCls]} /></td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                            {/* ONECHAMBER 자료 탐색 결과 */}
                            {resData.docSearchResult && (
                              <div className={styles.aiDocSearch}>
                                <div className={styles.aiDocSearchHead}>
                                  <div className={styles.aiDocSearchTitle}>{resData.docSearchResult.title}</div>
                                  <span className={styles.aiDocMatch}>{resData.docSearchResult.count}</span>
                                </div>
                                <div className={styles.aiDocList}>
                                  {resData.docSearchResult.docs.map((doc, di) => (
                                    <div key={di} className={styles.aiDocItem}>
                                      <span className={styles.aiDocType}>{doc.type}</span>
                                      <span className={styles.aiDocName}>{doc.name}</span>
                                      <button
                                        className={styles.aiDocPreviewBtn}
                                        onClick={() => { setMailSidePanelOpen(false); setMouPanelOpen(false); setDocPreviewIdx(doc.previewIdx); setDocPreviewOpen(true); }}
                                        title="미리보기"
                                      >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                        </svg>
                                      </button>
                                      <span className={styles.aiDocMatchBadge}>{doc.match}</span>
                                    </div>
                                  ))}
                                </div>
                                <div className={styles.aiJudgmentBox}>
                                  <div className={styles.aiJudgmentKicker}>AI 판단</div>
                                  <div className={styles.aiJudgmentTitle}>{resData.docSearchResult.judgmentTitle}</div>
                                  <div className={styles.aiJudgmentText}>{resData.docSearchResult.judgmentText}</div>
                                </div>
                              </div>
                            )}

                            {/* 메일 전용 UI (요약 + 본문 토글) */}
                            {resData.mailSummary && resData.mailBody && (
                              <MailBodyViewer
                                summary={resData.mailSummary}
                                body={resData.mailBody}
                              />
                            )}

                            {/* 리스 정보 패널 */}
                            {resData.leaseInfo && (
                              <div className={styles.leaseInfoBlock}>
                                <div className={styles.leaseInfoHeader}>
                                  <span className={styles.leaseInfoTitle}>{resData.leaseInfoTitle ?? '리스 정보'}</span>
                                  <button
                                    className={styles.leaseInfoBtn}
                                    onClick={() => (resData.leaseInfoBtnLabel ?? '상환 스케줄 확인') === '상환 스케줄 확인' && setRepaymentScheduleOpen(true)}
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>
                                    {resData.leaseInfoBtnLabel ?? '상환 스케줄 확인'}
                                  </button>
                                </div>
                                <div className={styles.leaseInfoBody}>
                                  {/* 1행: 리스료~회계처리방식 */}
                                  <AXKeyValueGrid
                                    columns={4}
                                    primaryFirst
                                    cellBackground="#EEF2FF"
                                    cellBorderColor="#C7D2FE"
                                    labelColor="#9CA3AF"
                                    valueColor="#3730A3"
                                    primaryValueColor="#4361EE"
                                    items={resData.leaseInfo.slice(0, 4)}
                                  />
                                  {/* 2행: 계약기간~결제수단 */}
                                  <div className={styles.leaseInfoRow2}>
                                    {resData.leaseInfo.slice(4, 8).map((item, i) => (
                                      <div key={i} className={styles.leaseInfoCell}>
                                        <span className={styles.leaseInfoLabel}>{item.label}</span>
                                        <div className={styles.leaseInfoBox}>
                                          <span className={styles.leaseInfoVal}>{item.value}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* 3행: 첨부파일 */}
                                  {resData.journalEntry?.attachment && (
                                    <div className={styles.leaseInfoAttachRow}>
                                      <span className={styles.leaseInfoLabel}>첨부 파일</span>
                                      <div
                                        className={styles.leaseInfoAttachChip}
                                        style={resData.journalEntry.attachment === '리스 계약서.PNG' ? { cursor: 'pointer' } : undefined}
                                        onClick={() => {
                                          if (resData.journalEntry?.attachment !== '리스 계약서.PNG') return;
                                          setMouPanelOpen(false);
                                          setMailSidePanelOpen(false);
                                          setDocPreviewOpen(false);
                                          setAttachmentPreview('lease');
                                        }}
                                      >
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                        <span>{resData.journalEntry.attachment}</span>
                                        <button className={styles.leaseInfoAttachDel} onClick={(e) => e.stopPropagation()}>×</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* 거래 정보 패널 (invoice-journal 등) */}
                            {resData.transactionInfo && (
                              <div className={styles.leaseInfoBlock}>
                                <div className={styles.leaseInfoHeader}>
                                  <span className={styles.leaseInfoTitle}>거래 정보</span>
                                  <button
                                    className={styles.leaseInfoBtn}
                                    onClick={() => {
                                      if (resData.transactionInfoBtnLabel !== '영수증 원본 확인') return;
                                      setMouPanelOpen(false);
                                      setMailSidePanelOpen(false);
                                      setDocPreviewOpen(false);
                                      setAttachmentPreview('receipt');
                                    }}
                                  >
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>
                                    {resData.transactionInfoBtnLabel ?? '원본 확인'}
                                  </button>
                                </div>
                                <div className={styles.leaseInfoBody}>
                                  <AXKeyValueGrid
                                    columns={4}
                                    primaryFirst
                                    cellBackground="#EEF2FF"
                                    cellBorderColor="#C7D2FE"
                                    labelColor="#9CA3AF"
                                    valueColor="#3730A3"
                                    primaryValueColor="#4361EE"
                                    items={resData.transactionInfo.slice(0, 4)}
                                  />
                                  <div className={styles.leaseInfoRow2}>
                                    {resData.transactionInfo.slice(4, 8).map((item, i) => (
                                      <div key={i} className={styles.leaseInfoCell}>
                                        <span className={styles.leaseInfoLabel}>{item.label}</span>
                                        <div className={styles.leaseInfoBox}>
                                          <span className={styles.leaseInfoVal}>{item.value}</span>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {resData.journalEntry?.attachment && (
                                    <div className={styles.leaseInfoAttachRow}>
                                      <span className={styles.leaseInfoLabel}>첨부 파일</span>
                                      <div className={styles.leaseInfoAttachChip}>
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                                        <span>{resData.journalEntry.attachment}</span>
                                        <button className={styles.leaseInfoAttachDel}>×</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            {/* 전표 분개 */}
                            {resData.journalEntry && (
                              <div className={styles.journalBlock}>
                                <div className={styles.journalHeader}>
                                  <span className={styles.journalTitle}>전표 분개</span>
                                  <button
                                    className={styles.journalEditBtn}
                                    onClick={() => setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, journalEditMode: !s.journalEditMode } : s))}
                                  >
                                    {slot.journalEditMode ? (
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                                    ) : (
                                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                    )}
                                    {slot.journalEditMode ? '완료' : '편집'}
                                  </button>
                                </div>
                                <div className={styles.journalBody}>
                                  {/* 전표 헤더 테이블 */}
                                  <table className={styles.journalMetaTable}>
                                    <thead>
                                      <tr>
                                        <th>전표 일자</th><th>구분</th><th>거래처</th><th>적요</th>
                                        <th className={styles.thRight}>금액</th><th className={styles.thRight}>세율</th><th className={styles.thRight}>세액</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      <tr>
                                        <td>{resData.journalEntry.date}</td>
                                        <td><span className={styles.journalCategoryBadge}>{resData.journalEntry.category}</span></td>
                                        <td>{resData.journalEntry.party}</td>
                                        <td>{resData.journalEntry.desc}</td>
                                        <td className={styles.tdRight}>{resData.journalEntry.amount}</td>
                                        <td className={styles.tdRight}>{resData.journalEntry.taxRate}</td>
                                        <td className={styles.tdRight}>{resData.journalEntry.taxAmt}</td>
                                      </tr>
                                    </tbody>
                                  </table>
                                  {/* 분개 상세 테이블 */}
                                  <table className={styles.journalDetailTable}>
                                    <thead>
                                      <tr>
                                        <th>구분</th><th>계정과목</th>
                                        <th className={styles.thRight}>차변(출금)</th><th className={styles.thRight}>대변(입금)</th>
                                        <th className={styles.thRight}>세율</th><th className={styles.thRight}>세액</th>
                                        <th>거래처</th><th>적요</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {resData.journalEntry.rows.map((row, ri) => (
                                        <tr key={ri}>
                                          <td><span className={`${styles.journalType} ${row.type === '차변' ? styles.journalDebit : styles.journalCredit}`}>{row.type}</span></td>
                                          {slot.journalEditMode ? (
                                            <>
                                              <td><input className={styles.journalEditCellInput} defaultValue={row.account} /></td>
                                              <td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.debit} /></td>
                                              <td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.credit} /></td>
                                              <td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.taxRate} /></td>
                                              <td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.taxAmt} /></td>
                                              <td><input className={styles.journalEditCellInput} defaultValue={row.party} /></td>
                                              <td><input className={styles.journalEditCellInput} defaultValue={row.desc} /></td>
                                            </>
                                          ) : (
                                            <>
                                              <td className={styles.journalAccount}>{row.account}</td>
                                              <td className={styles.tdRight}>{row.debit}</td>
                                              <td className={styles.tdRight}>{row.credit}</td>
                                              <td className={styles.tdRight}>{row.taxRate}</td>
                                              <td className={styles.tdRight}>{row.taxAmt}</td>
                                              <td>{row.party}</td>
                                              <td>{row.desc}</td>
                                            </>
                                          )}
                                        </tr>
                                      ))}
                                    </tbody>
                                    <tfoot>
                                      <tr className={styles.journalTotalRow}>
                                        <td colSpan={2} className={styles.journalTotalLabel}>합계</td>
                                        <td className={`${styles.tdRight} ${styles.journalTotalNum}`}>{resData.journalEntry.totals.debit}</td>
                                        <td className={`${styles.tdRight} ${styles.journalTotalNum}`}>{resData.journalEntry.totals.credit}</td>
                                        <td />
                                        <td className={`${styles.tdRight} ${styles.journalTotalNum}`}>{resData.journalEntry.totals.taxAmt}</td>
                                        <td /><td />
                                      </tr>
                                    </tfoot>
                                  </table>
                                  <div className={styles.journalActions}>
                                    <button
                                      className={`${styles.journalSendBtn} ${slot.journalSent ? styles.journalSendBtnDone : ''}`}
                                      disabled={slot.journalSent}
                                      onClick={() => {
                                        setSlots(prev => prev.map((s, i) => i === slotIdx ? { ...s, journalSent: true } : s));
                                        scrollToJournalSentMsg(slot.id);
                                      }}
                                    >
                                      {slot.journalSent ? '전표 전송 완료' : resData.journalEntry.actionLabel}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            )}

                            {/* 워크플로우 카드 그리드 */}
                            {resData.workflowCards.length > 0 && (
                              <div className={styles.workflowGrid}>
                                {resData.workflowCards.map((wc, wi) => (
                                  <div key={wi} className={styles.workflowCard}>
                                    <div className={styles.workflowCardTitle}>{wc.title}</div>
                                    <div className={styles.workflowCardDesc}>{wc.desc}</div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* 감지 현황 테이블 */}
                            {resData.tableRows.length > 0 && (
                              <div className={styles.detectionTable}>
                                <div className={styles.detectionTableHead}>
                                  <span>항목</span><span>상태</span><span>판단 근거</span><span>추천 조치</span>
                                </div>
                                {resData.tableRows.map((row, ri) => (
                                  <div key={ri} className={styles.detectionTableRow}>
                                    <span className={styles.dtLabel}>{row.label}</span>
                                    <span className={`${styles.dtStatus} ${styles[row.statusCls]}`}>{row.statusText}</span>
                                    <span className={styles.dtJudge}>{row.judge}</span>
                                    <span className={styles.dtAction}>{row.action}</span>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* 추천 조치 목록 */}
                            {resData.actionItems.length > 0 && (
                              <div className={styles.actionItemList}>
                                <div className={styles.actionItemListTitle}>AI 추천 조치</div>
                                {resData.actionItems.map((ai, aii) => (
                                  <div key={aii} className={styles.actionItem}>
                                    <div className={styles.actionItemNum}>{aii + 1}</div>
                                    <div className={styles.actionItemBody}>
                                      <div className={styles.actionItemTitle}>{ai.title}</div>
                                      <div className={styles.actionItemDesc}>{ai.desc}</div>
                                    </div>
                                    <div className={styles.actionItemScore}>{ai.score}</div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* 노트 (버튼 포함 가능) */}
                            {resData.note && (
                              <div className={styles.resultNote}>
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg>
                                <span>{resData.note}</span>
                                {resData.noteAction && (
                                  <button className={styles.openBtn} onClick={() => resData.noteAction!.path === '/oneffice/report' ? openReportPanel() : navigate(resData.noteAction!.path)}>{resData.noteAction.label}</button>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* 모듈 칩 — live 슬롯(실제 API 응답)은 표시 안 함 */}
                      {!slot.live && (
                      <div className={styles.moduleChips}>
                        {resData.moduleChips.map((chip, ci) => {
                          const isDone = chip.done && slot.erpDone;
                          const isMailChip = chip.label.startsWith('[메일');
                          return (
                            <div
                              key={ci}
                              className={`${styles.moduleChip} ${isDone ? styles.chipDone : styles.chipLoading} ${isMailChip && isDone ? styles.chipMailClickable : ''}`}
                              onClick={isMailChip && isDone ? () => { setMouPanelOpen(false); setDocPreviewOpen(false); setMailSidePanelOpen(true); setMailSidePanelLang('ko'); } : undefined}
                              style={isMailChip && isDone ? { cursor: 'pointer' } : undefined}
                            >
                              <SlotChipIcon iconType={chip.iconType} done={isDone} />
                              <span>{chip.label}</span>
                              {isDone ? (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                              ) : (
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M7 17L17 7M7 7h10v10"/></svg>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      )}

                    </div>

                    {/* 전표 전송 완료 AI 추가 응답 — ProActive AI 응답 카드 밖 */}
                    {resData.journalEntry && slot.journalSent && (
                      <div className={styles.journalSentMsgRow} ref={el => { journalSentMsgRefs.current.set(slot.id, el); }}>
                        <div className={styles.journalSentMsgAvatar}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/></svg>
                        </div>
                        <div className={styles.journalSentMsgBubble}>
                          전표입력으로 분개를 전송하였습니다.<br />전표입력 메뉴에서 분개를 확인해주세요.
                          <div>
                            <button
                              className={styles.journalSentMsgBtn}
                              onClick={() => { const path = JOURNAL_LINK_PATH[slot.responseKey]; if (path) navigate(path); }}
                            >
                              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>
                              전표 입력 바로가기
                            </button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* 다음 액션 말풍선 */}
                    {slot.bubblePhase !== 'hidden' && (
                      <div
                        ref={el => { slotBubbleRowRefs.current.set(slotIdx, el); }}
                        className={styles.nextActionRow}
                        onClick={() => slot.bubblePhase === 'complete' && goTo(1 + slotIdx + 1)}
                        style={{ cursor: slot.bubblePhase === 'complete' ? 'pointer' : 'default' }}
                      >
                        <div className={styles.nextActionAvatar}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff">
                            <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                          </svg>
                        </div>
                        <div className={styles.nextActionBubble}>
                          {slot.bubblePhase === 'loading' && (
                            <div className={styles.bubbleDots}>
                              <div className={styles.bubbleDot} />
                              <div className={styles.bubbleDot} />
                              <div className={styles.bubbleDot} />
                            </div>
                          )}
                          {(slot.bubblePhase === 'typing' || slot.bubblePhase === 'complete') && (
                            <>
                              <span>
                                {slot.bubbleText}
                                {slot.bubblePhase === 'typing' && <span className={styles.bubbleCursor} />}
                              </span>
                              {slot.bubblePhase === 'complete' && (
                                <svg className={styles.nextActionArrow} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M12 5v14M5 12l7 7 7-7"/>
                                </svg>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </>
                )}

              </div>
            </div>
          );
        })}

        {/* ══════════════════════════════════════════
            SECTION LAST — ProActive 제안 (항상 마지막 고정)
        ══════════════════════════════════════════ */}
        {(() => {
          const paIdx = slots.length + 1;
          return (
            <div
              ref={el => { sectionRefs.current[paIdx] = el; }}
              className={`${styles.section} ${styles.sectionPa}`}
              style={sectionStyle(paIdx)}
            >
              <div className={styles.innerProactive}>
                {/* 헤더: 배지 아이콘 + 제목/부제목 + 카운트 */}
                <div className={styles.proActiveSlotHeader}>
                  <div className={styles.proActiveHeadBadge}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/>
                    </svg>
                  </div>
                  <div className={styles.proActiveHeadText}>
                    <div className={styles.proActiveHeadTitle}>ProActive 제안</div>
                    <div className={styles.proActiveHeadSub}>AI가 지금 확인하면 좋을 업무를 골라뒀어요</div>
                  </div>
                  <span className={styles.proActiveHeadCount}>
                    <span className={styles.proActiveHeadCountDot} />
                    {currentProActiveCards.length}건
                  </span>
                </div>
                {/* 카드 수직 리스트 */}
                <div className={styles.proActiveSimpleGrid}>
                  {currentProActiveCards.map((card, cardIdx) => (
                    <ProActiveSlotCard
                      key={card.id}
                      card={card}
                      idx={cardIdx}
                      onClick={() => handleProActiveCardClick(card)}
                      onDismiss={() => handleProActiveCardDismiss(card)}
                      onSecondaryAction={
                        card.secondaryAction === 'mail-source'
                          ? () => { setMouMailSourceLang('ja'); setMouMailSourceOpen(true); }
                          : undefined
                      }
                    />
                  ))}
                </div>
              </div>
            </div>
          );
        })()}

      </div>{/* end .track */}

      {/* ── 우측 네비게이션 도트 ── */}
      <nav className={styles.navDots}>
        {sectionLabels.map((label, i) => {
          if (section0Collapsed && i === 0) return null; // section 0 collapse 시 홈 dot 숨김
          return (
            <button
              key={i}
              className={`${styles.navDot} ${i === activeIdx ? styles.navDotActive : ''}`}
              onClick={() => goTo(i)}
              title={label}
            >
              <span className={styles.navDotLabel}>{label}</span>
            </button>
          );
        })}
      </nav>

      {/* ── 히어로 화면일 때: 하단 peek veil + 바운스 화살표 ── */}
      {activeIdx === 0 && entered && !section0Collapsed && (
        <>
          <div className={styles.peekVeil} aria-hidden="true" />
          <button
            className={`${styles.scrollHintDown} ${scrolledDown ? styles.scrollHintHidden : ''}`}
            onClick={() => goTo(sectionCount - 1)}
            aria-label="ProActive 제안 보기"
            title="ProActive 제안 보기"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M6 9l6 6 6-6"/>
            </svg>
          </button>
        </>
      )}

      </> ) : (

        /* ══════════════════════════════════════════
            대화 모드 레이아웃 (단일 스크롤 + 플로팅 프롬프트)
        ══════════════════════════════════════════ */
        <div className={`${styles.convWrapper} ${mouPanelOpen ? styles.convWrapperMouOpen : ''} ${mailSidePanelOpen ? styles.convWrapperMailOpen : ''} ${docPreviewOpen ? styles.convWrapperDocOpen : ''} ${reportPanelOpen ? styles.convWrapperReportOpen : ''} ${attachmentPreview ? styles.convWrapperReceiptOpen : ''}`}>

          {/* 대화 스크롤 영역 */}
          <div ref={convScrollRef} className={styles.convScrollArea}>

            {/* ── 홈 프롬프트 경로: Section 0 응답 인라인 ── */}
            {(convPhase === 'streaming' || convPhase === 'done') && submittedQuery && (
              <>
                <div className={styles.convBubbleRow}>
                  <div className={`${styles.promptCard} ${styles.userMsgCard}`}>{submittedQuery}</div>
                </div>
                <div className={`${styles.responseCard} ${styles.convResponseCard}`}>
                  <div className={styles.responseHeader}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#3B82F6"><path d="M12 2l1.5 8.5L22 12l-8.5 1.5L12 22l-1.5-8.5L2 12l8.5-1.5z"/></svg>
                    <span className={`${styles.respTitle} ${styles.respTitleDone}`}>ProActive AI 응답 완료</span>
                  </div>
                  <div className={styles.responseBody}>
                    <div className={styles.resultContent}>
                      <p className={styles.resultIntro}>분석 결과, 이번주 미수금이 있는 거래처는 총 <strong>3곳</strong>이에요:</p>
                      <table className={styles.resultTable}>
                        <thead><tr><th>거래처명</th><th>미수금액(JPY)</th><th>마감일</th><th>담당자</th><th>상태</th></tr></thead>
                        <tbody>
                          <tr><td>㈜아사히 전자</td><td className={styles.tdAmount}>2,450,000</td><td>2025.08.25</td><td>田中 部長</td><td><AXStatusBadge size="sm" label="연체" {...AR_STATUS_STYLE.stOverdue} /></td></tr>
                          <tr><td>도쿄 물산 주식회사</td><td className={styles.tdAmount}>1,820,000</td><td>2025.08.28</td><td>佐藤 씨</td><td><AXStatusBadge size="sm" label="마감 임박" {...AR_STATUS_STYLE.stWarning} /></td></tr>
                          <tr><td>오사카 상사</td><td className={styles.tdAmount}>3,100,000</td><td>2025.08.30</td><td>山田 씨</td><td><AXStatusBadge size="sm" label="처리 중" {...AR_STATUS_STYLE.stProgress} /></td></tr>
                        </tbody>
                      </table>
                      <div className={styles.resultNote}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        <span>ONEFFICE 보고서 초안이 생성되었어요. 내용을 검토한 후 제출할 수 있어요.</span>
                        <button className={styles.openBtn} onClick={() => navigate('/oneffice/report')}>보고서 열기 →</button>
                      </div>
                    </div>
                  </div>
                  <div className={styles.moduleChips}>
                    <div className={`${styles.moduleChip} ${styles.chipDone}`}><SlotChipIcon iconType="bar" done={true} /><span>[ERP 회계 ▶ 거래내역]</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg></div>
                    <div className={`${styles.moduleChip} ${styles.chipDone}`}><SlotChipIcon iconType="doc" done={true} /><span>[ONEFFICE ▶ 보고서 초안]</span><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg></div>
                  </div>
                </div>
                {/* 홈 프롬프트 이후 AI 감지 업무 제안 카드 */}
                <div className={styles.convPostSuggestWrap}>
                  <div className={styles.postSuggestHeader}>
                    <div className={styles.postSuggestDot}/>
                    <span className={styles.postSuggestTitle}>AI가 추가로 감지한 업무 제안이에요</span>
                  </div>
                  <div className={styles.postSuggestGrid}>
                    {postResponseSuggestions.map((item, idx) => (
                      <div key={item.id} className={styles.postSuggestCardSlide} style={{ animationDelay: `${idx * 0.12}s` }}>
                        <PostSuggestCard item={item} idx={idx} onClick={item.id === 921 ? openMouReply : item.prompt ? () => handlePostSuggestClick(item) : undefined} onActionModal={setOpenActionModal} />
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* ── 슬롯 응답 선형 배치 ── */}
            {slots.filter(s => s.phase !== 'idle').map((slot) => {
              const resData = SLOT_RESPONSE_DATA[slot.responseKey] ?? SLOT_RESPONSE_DATA.custom;
              return (
                <div key={slot.id} className={styles.convSlotBlock}>

                  {/* 사용자 프롬프트 버블 */}
                  <div className={styles.convBubbleRow}>
                    <div className={styles.userMsgWrap}>
                      {/* 첨부 이미지 — 말풍선 위에 미리보기로 표시 */}
                      <div className={`${styles.promptCard} ${styles.userMsgCard}`}>
                        <div>{slot.prompt}</div>
                      </div>
                    </div>
                  </div>

                  {/* AI 응답 카드 */}
                  <div className={`${styles.responseCard} ${styles.convResponseCard}`}>
                    <div className={styles.responseHeader}>
                      <span className={styles.respHeaderIcon}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="#3B82F6"><path d="M12 2l1.5 8.5L22 12l-8.5 1.5L12 22l-1.5-8.5L2 12l8.5-1.5z"/></svg>
                      </span>
                      {slot.phase === 'streaming' ? (
                        slot.headerTitle ? (
                          <>
                            <span key={slot.headerStepIdx} className={`${styles.respTitle} ${styles.respTitleStep}`}>{slot.headerTitle}</span>
                            <span className={styles.respStepCount}>{(slot.headerStepIdx ?? 0) + 1} / {slot.headerStepTotal ?? 1}</span>
                            <span className={styles.hdrSteps}>
                              {Array.from({ length: slot.headerStepTotal ?? 1 }).map((_, k) => (
                                <span key={k} className={k < (slot.headerStepIdx ?? 0) ? styles.hdrStepDone : k === (slot.headerStepIdx ?? 0) ? styles.hdrStepActive : styles.hdrStep} />
                              ))}
                            </span>
                          </>
                        ) : (
                          <><span key={slot.live ? slot.liveStep : undefined} className={`${styles.respTitle} ${slot.live && slot.liveStep ? styles.respTitleStep : ''}`}>{(slot.live && slot.liveStep) || resData.streamingTitle || 'ProActive AI 응답 생성 중...'}</span><div className={styles.rDots}><div className={styles.rDot}/><div className={styles.rDot}/><div className={styles.rDot}/></div></>
                        )
                      ) : (
                        <span key={`lndone-${slot.id}`} className={`${styles.respTitle} ${slot.headerDoneGreen ? styles.respTitleGreen : styles.respTitleDone}`}>{resData.doneTitle || 'ProActive AI 응답 완료'}</span>
                      )}
                    </div>
                    <div className={styles.responseBody}>
                      {slot.live ? (
                        <LiveResponseBody slot={slot} />
                      ) : slot.phase === 'streaming' ? (
                        <><p className={styles.streamingText}>{resData.streamingText}<span className={styles.rCursor}/></p><div className={styles.skeletons}><div className={styles.skelLine} style={{ width: '72%' }}/><div className={styles.skelLine} style={{ width: '58%' }}/><div className={styles.skelLine} style={{ width: '85%' }}/></div></>
                      ) : (
                        <div className={styles.resultContent}>
                          {resData.insightCard && (
                            <div className={styles.insightCard}>
                              <div className={styles.insightCardLabel}>{resData.insightCard.label}</div>
                              <div className={styles.insightCardTitle}>{resData.insightCard.title}</div>
                              <div className={styles.insightCardText}>{resData.insightCard.text}</div>
                            </div>
                          )}
                          {resData.briefingCard && (
                            <div className={styles.briefingCard}>
                              <div className={styles.briefingCardLabel}>{resData.briefingCard.label}</div>
                              <div className={styles.briefingCardTitle}>{resData.briefingCard.title}</div>
                              <div className={styles.briefingCardRows}>
                                {resData.briefingCard.rows.map((row, i) => (
                                  <div key={i} className={styles.briefingCardRow}>
                                    <span className={styles.briefingCardRowKey}>{row.key}</span>
                                    <span className={styles.briefingCardRowVal}>{row.value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          <p className={styles.resultIntro}>{resData.resultText}</p>
                          {/* 거래처 미수금 테이블 (ar-report) */}
                          {resData.arTable && resData.arTable.length > 0 && (
                            <table className={styles.resultTable}>
                              <thead><tr><th>거래처명</th><th>미수금액(JPY)</th><th>마감일</th><th>담당자</th><th>상태</th></tr></thead>
                              <tbody>
                                {resData.arTable.map((row, ri) => (
                                  <tr key={ri}>
                                    <td>{row.name}</td>
                                    <td className={styles.tdAmount}>{row.amount}</td>
                                    <td>{row.dueDate}</td>
                                    <td>{row.person}</td>
                                    <td><AXStatusBadge size="sm" label={row.statusText} {...AR_STATUS_STYLE[row.statusCls]} /></td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                          {resData.docSearchResult && (
                            <div className={styles.aiDocSearch}>
                              <div className={styles.aiDocSearchHead}>
                                <div className={styles.aiDocSearchTitle}>{resData.docSearchResult.title}</div>
                                <span className={styles.aiDocMatch}>{resData.docSearchResult.count}</span>
                              </div>
                              <div className={styles.aiDocList}>
                                {resData.docSearchResult.docs.map((doc, di) => (
                                  <div key={di} className={styles.aiDocItem}>
                                    <span className={styles.aiDocType}>{doc.type}</span>
                                    <span className={styles.aiDocName}>{doc.name}</span>
                                    <button
                                      className={styles.aiDocPreviewBtn}
                                      onClick={() => { setMailSidePanelOpen(false); setMouPanelOpen(false); setDocPreviewIdx(doc.previewIdx); setDocPreviewOpen(true); }}
                                      title="미리보기"
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                                      </svg>
                                    </button>
                                    <span className={styles.aiDocMatchBadge}>{doc.match}</span>
                                  </div>
                                ))}
                              </div>
                              <div className={styles.aiJudgmentBox}>
                                <div className={styles.aiJudgmentKicker}>AI 판단</div>
                                <div className={styles.aiJudgmentTitle}>{resData.docSearchResult.judgmentTitle}</div>
                                <div className={styles.aiJudgmentText}>{resData.docSearchResult.judgmentText}</div>
                              </div>
                            </div>
                          )}
                          {resData.mailSummary && resData.mailBody && (<MailBodyViewer summary={resData.mailSummary} body={resData.mailBody} />)}
                          {resData.leaseInfo && (
                            <div className={styles.leaseInfoBlock}>
                              <div className={styles.leaseInfoHeader}><span className={styles.leaseInfoTitle}>{resData.leaseInfoTitle ?? '리스 정보'}</span><button className={styles.leaseInfoBtn} onClick={() => (resData.leaseInfoBtnLabel ?? '상환 스케줄 확인') === '상환 스케줄 확인' && setRepaymentScheduleOpen(true)}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>{resData.leaseInfoBtnLabel ?? '상환 스케줄 확인'}</button></div>
                              <div className={styles.leaseInfoBody}>
                                <AXKeyValueGrid columns={4} primaryFirst cellBackground="#EEF2FF" cellBorderColor="#C7D2FE" labelColor="#9CA3AF" valueColor="#3730A3" primaryValueColor="#4361EE" items={resData.leaseInfo.slice(0,4)} />
                                <div className={styles.leaseInfoRow2}>{resData.leaseInfo.slice(4,8).map((item,i)=>(<div key={i} className={styles.leaseInfoCell}><span className={styles.leaseInfoLabel}>{item.label}</span><div className={styles.leaseInfoBox}><span className={styles.leaseInfoVal}>{item.value}</span></div></div>))}</div>
                                {resData.journalEntry?.attachment&&(<div className={styles.leaseInfoAttachRow}><span className={styles.leaseInfoLabel}>첨부 파일</span><div className={styles.leaseInfoAttachChip} style={resData.journalEntry.attachment==='리스 계약서.PNG'?{cursor:'pointer'}:undefined} onClick={()=>{ if(resData.journalEntry?.attachment!=='리스 계약서.PNG') return; setMouPanelOpen(false); setMailSidePanelOpen(false); setDocPreviewOpen(false); setAttachmentPreview('lease'); }}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg><span>{resData.journalEntry.attachment}</span><button className={styles.leaseInfoAttachDel} onClick={(e)=>e.stopPropagation()}>×</button></div></div>)}
                              </div>
                            </div>
                          )}
                          {resData.transactionInfo && (
                            <div className={styles.leaseInfoBlock}>
                              <div className={styles.leaseInfoHeader}><span className={styles.leaseInfoTitle}>거래 정보</span><button className={styles.leaseInfoBtn} onClick={() => { if (resData.transactionInfoBtnLabel !== '영수증 원본 확인') return; setMouPanelOpen(false); setMailSidePanelOpen(false); setDocPreviewOpen(false); setAttachmentPreview('receipt'); }}><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>{resData.transactionInfoBtnLabel ?? '원본 확인'}</button></div>
                              <div className={styles.leaseInfoBody}>
                                <AXKeyValueGrid columns={4} primaryFirst cellBackground="#EEF2FF" cellBorderColor="#C7D2FE" labelColor="#9CA3AF" valueColor="#3730A3" primaryValueColor="#4361EE" items={resData.transactionInfo.slice(0,4)} />
                                <div className={styles.leaseInfoRow2}>{resData.transactionInfo.slice(4,8).map((item,i)=>(<div key={i} className={styles.leaseInfoCell}><span className={styles.leaseInfoLabel}>{item.label}</span><div className={styles.leaseInfoBox}><span className={styles.leaseInfoVal}>{item.value}</span></div></div>))}</div>
                                {resData.journalEntry?.attachment&&(<div className={styles.leaseInfoAttachRow}><span className={styles.leaseInfoLabel}>첨부 파일</span><div className={styles.leaseInfoAttachChip}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg><span>{resData.journalEntry.attachment}</span><button className={styles.leaseInfoAttachDel}>×</button></div></div>)}
                              </div>
                            </div>
                          )}
                          {resData.journalEntry && (
                            <div className={styles.journalBlock}>
                              <div className={styles.journalHeader}>
                                <span className={styles.journalTitle}>전표 분개</span>
                                <button
                                  className={styles.journalEditBtn}
                                  onClick={() => setSlots(prev => prev.map(s => s.id === slot.id ? { ...s, journalEditMode: !s.journalEditMode } : s))}
                                >
                                  {slot.journalEditMode ? (
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                                  ) : (
                                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                                  )}
                                  {slot.journalEditMode ? '완료' : '편집'}
                                </button>
                              </div>
                              <div className={styles.journalBody}>
                                <table className={styles.journalMetaTable}><thead><tr><th>전표 일자</th><th>구분</th><th>거래처</th><th>적요</th><th className={styles.thRight}>금액</th><th className={styles.thRight}>세율</th><th className={styles.thRight}>세액</th></tr></thead><tbody><tr><td>{resData.journalEntry.date}</td><td><span className={styles.journalCategoryBadge}>{resData.journalEntry.category}</span></td><td>{resData.journalEntry.party}</td><td>{resData.journalEntry.desc}</td><td className={styles.tdRight}>{resData.journalEntry.amount}</td><td className={styles.tdRight}>{resData.journalEntry.taxRate}</td><td className={styles.tdRight}>{resData.journalEntry.taxAmt}</td></tr></tbody></table>
                                <table className={styles.journalDetailTable}><thead><tr><th>구분</th><th>계정과목</th><th className={styles.thRight}>차변(출금)</th><th className={styles.thRight}>대변(입금)</th><th className={styles.thRight}>세율</th><th className={styles.thRight}>세액</th><th>거래처</th><th>적요</th></tr></thead><tbody>{resData.journalEntry.rows.map((row,ri)=>(<tr key={ri}><td><span className={`${styles.journalType} ${row.type==='차변'?styles.journalDebit:styles.journalCredit}`}>{row.type}</span></td>{slot.journalEditMode ? (<><td><input className={styles.journalEditCellInput} defaultValue={row.account} /></td><td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.debit} /></td><td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.credit} /></td><td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.taxRate} /></td><td className={styles.tdRight}><input className={styles.journalEditCellInput} defaultValue={row.taxAmt} /></td><td><input className={styles.journalEditCellInput} defaultValue={row.party} /></td><td><input className={styles.journalEditCellInput} defaultValue={row.desc} /></td></>) : (<><td className={styles.journalAccount}>{row.account}</td><td className={styles.tdRight}>{row.debit}</td><td className={styles.tdRight}>{row.credit}</td><td className={styles.tdRight}>{row.taxRate}</td><td className={styles.tdRight}>{row.taxAmt}</td><td>{row.party}</td><td>{row.desc}</td></>)}</tr>))}</tbody><tfoot><tr className={styles.journalTotalRow}><td colSpan={2} className={styles.journalTotalLabel}>합계</td><td className={`${styles.tdRight} ${styles.journalTotalNum}`}>{resData.journalEntry.totals.debit}</td><td className={`${styles.tdRight} ${styles.journalTotalNum}`}>{resData.journalEntry.totals.credit}</td><td/><td className={`${styles.tdRight} ${styles.journalTotalNum}`}>{resData.journalEntry.totals.taxAmt}</td><td/><td/></tr></tfoot></table>
                                <div className={styles.journalActions}>
                                  <button
                                    className={`${styles.journalSendBtn} ${slot.journalSent ? styles.journalSendBtnDone : ''}`}
                                    disabled={slot.journalSent}
                                    onClick={() => {
                                      setSlots(prev => prev.map(s => s.id === slot.id ? { ...s, journalSent: true } : s));
                                      scrollToJournalSentMsg(slot.id);
                                    }}
                                  >
                                    {slot.journalSent ? '전표 전송 완료' : resData.journalEntry.actionLabel}
                                  </button>
                                </div>
                              </div>
                            </div>
                          )}
                          {resData.workflowCards.length>0&&(<div className={styles.workflowGrid}>{resData.workflowCards.map((wc,wi)=>(<div key={wi} className={styles.workflowCard}><div className={styles.workflowCardTitle}>{wc.title}</div><div className={styles.workflowCardDesc}>{wc.desc}</div></div>))}</div>)}
                          {resData.tableRows.length>0&&(<div className={styles.detectionTable}><div className={styles.detectionTableHead}><span>항목</span><span>상태</span><span>판단 근거</span><span>추천 조치</span></div>{resData.tableRows.map((row,ri)=>(<div key={ri} className={styles.detectionTableRow}><span className={styles.dtLabel}>{row.label}</span><span className={`${styles.dtStatus} ${styles[row.statusCls]}`}>{row.statusText}</span><span className={styles.dtJudge}>{row.judge}</span><span className={styles.dtAction}>{row.action}</span></div>))}</div>)}
                          {resData.actionItems.length>0&&(<div className={styles.actionItemList}><div className={styles.actionItemListTitle}>AI 추천 조치</div>{resData.actionItems.map((ai,aii)=>(<div key={aii} className={styles.actionItem}><div className={styles.actionItemNum}>{aii+1}</div><div className={styles.actionItemBody}><div className={styles.actionItemTitle}>{ai.title}</div><div className={styles.actionItemDesc}>{ai.desc}</div></div><div className={styles.actionItemScore}>{ai.score}</div></div>))}</div>)}
                          {resData.note&&(<div className={styles.resultNote}><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg><span>{resData.note}</span>{resData.noteAction&&(<button className={styles.openBtn} onClick={()=>resData.noteAction!.path==='/oneffice/report'?openReportPanel():navigate(resData.noteAction!.path)}>{resData.noteAction.label}</button>)}</div>)}
                          {resData.postSuggestCards.length > 0 && (
                            <div className={styles.inlineSuggestSection}>
                              <div className={styles.inlineSuggestHead}>ProActive 후속 제안</div>
                              <div className={styles.postSuggestGrid}>
                                {resData.postSuggestCards.map((item, idx) => (
                                  <div
                                    key={item.id}
                                    className={styles.inlineSuggestCard}
                                    style={{ animationDelay: `${idx * 0.15}s` }}
                                    onClick={item.id === 921 ? openMouReply : item.prompt ? () => handlePostSuggestClick(item) : item.actionModal ? () => setOpenActionModal(item.actionModal!) : undefined}
                                  >
                                    <div className={styles.inlineSuggestCardTop}>
                                      <span className={styles.inlineSuggestCardTitle}>{item.title}</span>
                                      <span className={styles.inlineSuggestCardArrow}>→</span>
                                    </div>
                                    <div className={styles.inlineSuggestCardDesc}>{item.desc}</div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    {!slot.live && (
                    <div className={styles.moduleChips}>
                      {resData.moduleChips.map((chip, ci) => {
                        const isDone = chip.done && slot.erpDone;
                        const isMailChip = chip.label.startsWith('[메일');
                        return (
                          <div
                            key={ci}
                            className={`${styles.moduleChip} ${isDone ? styles.chipDone : styles.chipLoading} ${isMailChip && isDone ? styles.chipMailClickable : ''}`}
                            onClick={isMailChip && isDone ? () => { setMouPanelOpen(false); setDocPreviewOpen(false); setMailSidePanelOpen(true); setMailSidePanelLang('ko'); } : undefined}
                            style={isMailChip && isDone ? { cursor: 'pointer' } : undefined}
                          >
                            <SlotChipIcon iconType={chip.iconType} done={isDone} />
                            <span>{chip.label}</span>
                            {isDone ? (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>
                            ) : (
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M7 17L17 7M7 7h10v10"/></svg>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    )}
                  </div>

                  {/* 전표 전송 완료 AI 추가 응답 — ProActive AI 응답 카드 밖 */}
                  {resData.journalEntry && slot.journalSent && (
                    <div className={styles.journalSentMsgRow} ref={el => { journalSentMsgRefs.current.set(slot.id, el); }}>
                      <div className={styles.journalSentMsgAvatar}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M12 2 L13.5 10.5 L22 12 L13.5 13.5 L12 22 L10.5 13.5 L2 12 L10.5 10.5 Z"/></svg>
                      </div>
                      <div className={styles.journalSentMsgBubble}>
                        전표입력으로 분개를 전송하였습니다.<br />전표입력 메뉴에서 분개를 확인해주세요.
                        <div>
                          <button
                            className={styles.journalSentMsgBtn}
                            onClick={() => { const path = JOURNAL_LINK_PATH[slot.responseKey]; if (path) navigate(path); }}
                          >
                            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6M15 3h6v6M10 14L21 3"/></svg>
                            전표 입력 바로가기
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                </div>
              );
            })}

          </div>{/* end convScrollArea */}

          {/* ── 플로팅 프롬프트 바 ── */}
          <div className={styles.floatingPromptBar}>
            <div className={styles.floatingPromptInner}>
              <AttachmentStrip attachments={attachments} notice={attachNotice} onRemove={removeAttachment} />
              {/* 상단: textarea */}
              <div className={styles.convInputTop}>
                <textarea
                  ref={newTextareaRef}
                  className={styles.convInputField}
                  rows={1}
                  placeholder="추가로 질문하거나 업무를 요청해보세요..."
                  value={newQuery}
                  onChange={e => setNewQuery(e.target.value)}
                  onFocus={() => setNewPromptFocused(true)}
                  onBlur={() => setNewPromptFocused(false)}
                  onCompositionStart={() => { isComposingRef.current = true; }}
                  onCompositionEnd={() => { isComposingRef.current = false; }}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      if (isComposingRef.current || e.nativeEvent.isComposing) return;
                      e.preventDefault();
                      handleConvModeSend();
                    }
                  }}
                />
              </div>
              {/* 하단: 액션 버튼 */}
              <div className={styles.convInputBottom}>
                <div className={styles.convInputBottomLeft}>
                  {/* ── 파일 첨부 드롭다운 ── */}
                  <div className={styles.attachWrap} data-attach-ui onClick={e => e.stopPropagation()}>
                    <button
                      className={`${styles.convInputIconBtn} ${attachDropdownOpen === 'conv' ? styles.attachBtnActive : ''}`}
                      title="자료 첨부"
                      onClick={() => setAttachDropdownOpen(prev => prev === 'conv' ? null : 'conv')}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/></svg>
                    </button>
                    <div className={`${styles.attachDropdown} ${attachDropdownOpen === 'conv' ? styles.attachDropdownOpen : ''}`}>
                      {/* PC에서 첨부 */}
                      <button className={styles.attachOption} onClick={() => { setAttachDropdownOpen(null); convFileInputRef.current?.click(); }}>
                        <span className={styles.attachOptionIcon}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="4" width="18" height="13" rx="2"/><path d="M8 21h8M12 17v4"/>
                          </svg>
                        </span>
                        <span className={styles.attachOptionText}>
                          <strong>PC에서 첨부하기</strong>
                          <span>내 컴퓨터의 파일 선택</span>
                        </span>
                      </button>
                      {/* ONECHAMBER에서 첨부 */}
                      <button className={styles.attachOption} onClick={() => {
                        setAttachDropdownOpen(null);
                        setAttachments(prev => [...prev, {
                          uid: newAttachmentUid(), name: 'ONECHAMBER_신규사업_시장조사_2024.pptx', kind: 'doc' as const,
                        }]);
                        newTextareaRef.current?.focus();
                      }}>
                        <span className={`${styles.attachOptionIcon} ${styles.attachOptionIconOC}`}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
                          </svg>
                        </span>
                        <span className={styles.attachOptionText}>
                          <strong>ONECHAMBER에서 첨부하기</strong>
                          <span>업무 파일 검색 및 선택</span>
                        </span>
                      </button>
                    </div>
                    {/* 숨긴 파일 input */}
                    <input
                      ref={convFileInputRef}
                      type="file"
                      hidden
                      multiple
                      onChange={e => {
                        const rawFiles = Array.from(e.target.files ?? []);
                        e.target.value = '';
                        newTextareaRef.current?.focus();
                        // 문서는 RAG 색인, 이미지는 채팅 요청에 직접 싣는다
                        attachFiles(rawFiles);
                      }}
                    />
                  </div>
                </div>
                <div className={styles.convInputBottomRight}>
                  <LLMSelector dropUp={true} />
                  <button className={styles.convInputIconBtn} title="음성 입력">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3zM19 10v2a7 7 0 0 1-14 0v-2M12 19v4M8 23h8"/></svg>
                  </button>
                  <button className={styles.sendCircleBtn} onClick={handleConvModeSend} title="전송">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4 20-7z"/></svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

        </div> /* end convWrapper */
      )}

      {/* ── 실행 모달 ── */}
      {openActionModal && (
        <ActionModal
          config={openActionModal}
          onClose={() => setOpenActionModal(null)}
        />
      )}

      {/* ── 메일 원문 우측 슬라이드 패널 ── */}
      <div className={`${styles.mailSidePanel} ${mailSidePanelOpen ? styles.mailSidePanelOpen : ''}`}>
        {/* 헤더 */}
        <div className={styles.mailSideHdr}>
          <div className={styles.mailSideHdrIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
            </svg>
          </div>
          <div className={styles.mailSideHdrInfo}>
            <div className={styles.mailSideHdrKicker}>PARTNER MAIL · AUTO TRANSLATED</div>
            <div className={styles.mailSideHdrTitle}>Enterprise AI / Sovereign AI 제휴 관련 메일</div>
            <div className={styles.mailSideHdrSub}>한국어 번역을 먼저 표시합니다. 필요하면 일본어 원문으로 전환할 수 있습니다.</div>
          </div>
          <button className={styles.mailSideClose} onClick={() => setMailSidePanelOpen(false)}>×</button>
        </div>

        {/* 발신 메타 */}
        <div className={styles.mailSideMeta}>
          <span className={styles.mailSideMetaLabel}>보낸사람</span>
          <span className={styles.mailSideMetaVal}>타카하시 켄지 본부장 · Tokyo Strategic Partners</span>
          <span className={styles.mailSideMetaLabel}>받은시간</span>
          <span className={styles.mailSideMetaVal}>오늘 09:18 · 일본어 메일</span>
          <span className={styles.mailSideMetaLabel}>제목</span>
          <span className={`${styles.mailSideMetaVal} ${styles.mailSideMetaValBold}`}>Enterprise AI 및 Sovereign AI 협업에 관한 MOU 초안 요청</span>
        </div>

        {/* 언어 탭 */}
        <div className={styles.mailSideTabs}>
          <button
            className={`${styles.mailSideTab} ${mailSidePanelLang === 'ja' ? styles.mailSideTabActive : ''}`}
            onClick={() => setMailSidePanelLang('ja')}
          >일본어 원문</button>
          <button
            className={`${styles.mailSideTab} ${mailSidePanelLang === 'ko' ? styles.mailSideTabActive : ''}`}
            onClick={() => setMailSidePanelLang('ko')}
          >한국어 번역</button>
        </div>

        {/* 본문 */}
        <div className={styles.mailSideBody}>
          {mailSidePanelLang === 'ko' ? (
            <>
              <div className={styles.mailSideAiNote}>✦ 일본어 원문을 그대로 보존했습니다. 한국어 번역은 동일한 내용을 사용자 언어로 확인하기 위한 보조 보기입니다.</div>
              <div className={styles.mailSideText}>{`김민준 차장님께,

지난주 온라인 미팅에서 귀사의 Enterprise AI Platform 및 Sovereign AI 추진 방향에 대해 상세히 설명해 주셔서 감사합니다. 특히 회계·세무·문서관리·커뮤니케이션 데이터가 하나의 업무 흐름으로 연결되고, AI가 사용자의 다음 업무를 선제적으로 제안한다는 점이 매우 인상적이었습니다.

당사는 일본 내 중견기업 고객을 대상으로 회계/세무 운영 자동화와 내부 문서관리 체계 개선을 지원하고 있으며, 최근 고객사들로부터 "일본 내 데이터 주권을 보장하면서도 업무 자동화를 고도화할 수 있는 AI 플랫폼"에 대한 문의가 빠르게 증가하고 있습니다.

이에 양사 간 파트너십 가능성을 구체적으로 검토하고자 합니다. 우선 Enterprise AI 및 Sovereign AI 협력을 위한 MOU 초안을 받아볼 수 있을까요? 가능하다면 다음 내용을 포함해 주시면 감사하겠습니다.

1. 양사의 역할과 책임 범위
2. 일본 시장 내 공동 영업 및 PoC 추진 방식
3. 고객 데이터 보관 위치 및 보안/컴플라이언스 기준
4. 회계·세무 업무 자동화 영역에서의 주요 레퍼런스
5. 향후 기술 검증 및 공동 웨비나 추진 일정

또한 귀사가 보유한 관련 소개자료, 일본 시장 적용 가능성이 있는 레퍼런스 문서, 기술 사양서가 있다면 함께 공유 부탁드립니다. 내부 검토 후 다음 주 중 화상회의를 통해 구체적인 협력 범위를 논의하고 싶습니다.

확인 부탁드리며, 회신 기다리겠습니다.`}</div>
            </>
          ) : (
            <div className={styles.mailSideText}>{`金 民俊 様

先週のオンラインミーティングでは、貴社のEnterprise AI PlatformおよびSovereign AIの推進方針について、詳細にご説明いただき誠にありがとうございました。特に、会計・税務・文書管理・コミュニケーションデータが一つの業務フローとして連携し、AIがユーザーの次の業務を先回りして提案するという点に、大変強い関心を持ちました。

弊社では現在、日本国内の中堅企業のお客様を対象に、会計・税務業務の自動化および社内文書管理体制の改善支援を行っております。最近では、お客様から「日本国内におけるデータ主権を担保しながら、業務自動化を高度化できるAIプラットフォーム」に関する相談が急速に増加しております。

つきましては、両社間のパートナーシップの可能性を具体的に検討したく、まずはEnterprise AIおよびSovereign AI連携に関するMOUドラフトをご共有いただくことは可能でしょうか。

1. 両社の役割および責任範囲
2. 日本市場における共同営業およびPoC推進方法
3. 顧客データの保管場所ならびにセキュリティ／コンプライアンス基準
4. 会計・税務業務自動化領域における主要リファレンス
5. 今後の技術検証および共同ウェビナーの実施スケジュール

ご確認のほど、何卒よろしくお願い申し上げます。

タカハシ ケンジ
Tokyo Strategic Partners
本部長
Tel: +81-3-XXXX-XXXX`}</div>
          )}
        </div>

        {/* 푸터 */}
        <div className={styles.mailSideFooter}>
          <button className={styles.mailSideBtn} onClick={() => setMailSidePanelOpen(false)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
            </svg>
            메일함 바로가기
          </button>
          <button className={`${styles.mailSideBtn} ${styles.mailSideBtnPrimary}`} onClick={() => setMailSidePanelOpen(false)}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round">
              <path d="M3 10h10a8 8 0 018 8v2M3 10l6 6M3 10l6-6"/>
            </svg>
            답장 쓰기 ✏
          </button>
        </div>
      </div>

      {/* ── 문서 미리보기 우측 패널 (ai-doc-preview-btn 클릭) ── */}
      <div className={`${styles.docPreviewPanel} ${docPreviewOpen ? styles.docPreviewPanelOpen : ''}`}>
        {/* 헤더 */}
        <div className={styles.docPreviewHdr}>
          <div className={styles.docPreviewHdrIcon}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
            </svg>
          </div>
          <div className={styles.docPreviewHdrInfo}>
            <div className={styles.docPreviewHdrKicker}>
              ONECHAMBER · {DOC_PREVIEWS[docPreviewIdx]?.type ?? ''} 문서 미리보기
            </div>
            <div className={styles.docPreviewHdrTitle}>
              {DOC_PREVIEWS[docPreviewIdx]?.title ?? ''}
            </div>
          </div>
          <button className={styles.docPreviewClose} onClick={() => setDocPreviewOpen(false)}>×</button>
        </div>

        {/* 메타 정보 */}
        {DOC_PREVIEWS[docPreviewIdx] && (
          <div className={styles.docPreviewMeta}>
            <div className={styles.docPreviewMetaItem}>
              <span className={styles.docPreviewMetaLabel}>위치</span>
              <span className={styles.docPreviewMetaVal}>{DOC_PREVIEWS[docPreviewIdx].path}</span>
            </div>
            <div className={styles.docPreviewMetaItem}>
              <span className={styles.docPreviewMetaLabel}>크기</span>
              <span className={styles.docPreviewMetaVal}>{DOC_PREVIEWS[docPreviewIdx].size}</span>
            </div>
            <div className={styles.docPreviewMetaItem}>
              <span className={styles.docPreviewMetaLabel}>작성자</span>
              <span className={styles.docPreviewMetaVal}>{DOC_PREVIEWS[docPreviewIdx].author}</span>
            </div>
            <div className={styles.docPreviewMetaItem}>
              <span className={styles.docPreviewMetaLabel}>수정일</span>
              <span className={styles.docPreviewMetaVal}>{DOC_PREVIEWS[docPreviewIdx].date}</span>
            </div>
          </div>
        )}

        {/* 본문 */}
        {DOC_PREVIEWS[docPreviewIdx] && (() => {
          const d = DOC_PREVIEWS[docPreviewIdx];
          return (
            <div className={styles.docPreviewBody}>
              {/* AI 연관 이유 */}
              <div className={`${styles.docPreviewSection} ${styles.dpAiReason}`}>
                <div className={styles.dpAiReasonHdr}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round">
                    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                  </svg>
                  <span>AI 연관 이유</span>
                  <span className={styles.dpMatchPct}>{d.match}% 일치</span>
                </div>
                <div className={styles.dpMatchBarWrap}>
                  <div className={styles.dpMatchBar} style={{ width: `${d.match}%` }} />
                </div>
                <p className={styles.docPreviewPara} style={{ marginTop: 10 }}>{d.aiReason}</p>
                <div className={styles.dpKeywords}>
                  {d.keywords.map((kw, ki) => (
                    <AXTagChip
                      key={ki}
                      label={kw}
                      variant="readonly"
                      background="#fff"
                      textColor="#6D28D9"
                      borderColor="#C4B5FD"
                    />
                  ))}
                </div>
              </div>

              {/* 문서 개요 */}
              <div className={styles.docPreviewSection}>
                <div className={styles.docPreviewSectionTitle}>문서 개요</div>
                <p className={styles.docPreviewPara}>{d.overview}</p>
                <ul className={styles.dpBullets}>
                  {d.overviewBullets.map((b, bi) => <li key={bi}>{b}</li>)}
                </ul>
              </div>

              {/* 테이블 */}
              <div className={styles.docPreviewSection}>
                <div className={styles.docPreviewSectionTitle}>{d.tableTitle}</div>
                <table className={styles.docPreviewTable}>
                  <thead>
                    <tr>{d.tableHeaders.map((h, hi) => <th key={hi}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {d.tableRows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((cell, ci) => {
                          if (d.type === 'PPTX' && ci === 2) {
                            const statusMap: Record<string, { color: string; label: string }> = {
                              done: { color: '#10B981', label: '완료' },
                              progress: { color: '#F59E0B', label: '진행 중' },
                            };
                            const s = statusMap[cell] ?? { color: '#64748B', label: cell };
                            return <td key={ci} style={{ color: s.color, fontWeight: 600 }}>{s.label}</td>;
                          }
                          return <td key={ci}>{cell}</td>;
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* 푸터 */}
        <div className={styles.docPreviewFooter}>
          <button className={styles.docPreviewBtn} onClick={() => setDocPreviewOpen(false)}>닫기</button>
          <button className={`${styles.docPreviewBtn} ${styles.docPreviewBtnPrimary}`}>ONECHAMBER에서 열기 →</button>
        </div>
      </div>

      {/* ── 첨부 파일 원본 이미지 우측 패널 ── */}
      <div className={`${styles.receiptPreviewPanel} ${attachmentPreview ? styles.receiptPreviewPanelOpen : ''}`}>
        {(() => {
          const preview = attachmentPreview ? ATTACHMENT_PREVIEWS[attachmentPreview] : null;
          if (!preview) return null;
          return (
            <>
              {/* 헤더 */}
              <div className={styles.docPreviewHdr}>
                <div className={styles.docPreviewHdrIcon}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48"/>
                  </svg>
                </div>
                <div className={styles.docPreviewHdrInfo}>
                  <div className={styles.docPreviewHdrKicker}>{preview.kicker}</div>
                  <div className={styles.docPreviewHdrTitle}>{preview.fileName}</div>
                </div>
                <button className={styles.docPreviewClose} onClick={() => setAttachmentPreview(null)}>×</button>
              </div>

              {/* 메타 정보 */}
              <div className={styles.docPreviewMeta}>
                {preview.meta.map((m, mi) => (
                  <div className={styles.docPreviewMetaItem} key={mi}>
                    <span className={styles.docPreviewMetaLabel}>{m.label}</span>
                    <span className={styles.docPreviewMetaVal}>{m.value}</span>
                  </div>
                ))}
                <div className={styles.docPreviewMetaItem}>
                  <span className={styles.docPreviewMetaLabel}>파일 형식</span>
                  <span className={styles.docPreviewMetaVal}>{preview.fileType}</span>
                </div>
              </div>

              {/* 이미지 본문 */}
              <div className={styles.receiptPreviewBody}>
                <img src={preview.img} alt={preview.fileName} className={styles.receiptPreviewImg} />
                <div className={styles.receiptPreviewMetaBox}>
                  {preview.detail.map((d, di) => (
                    <div className={styles.receiptPreviewMetaRow} key={di}>
                      <span className={styles.receiptPreviewMetaLabel}>{d.label}</span>
                      <span className={styles.receiptPreviewMetaVal}>{d.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* 푸터 */}
              <div className={styles.docPreviewFooter}>
                <button className={styles.docPreviewBtn} onClick={() => setAttachmentPreview(null)}>닫기</button>
              </div>
            </>
          );
        })()}
      </div>

      {/* ── MOU 초안 우측 패널 ── */}
      <div className={`${styles.mouPanel} ${mouPanelOpen ? styles.mouPanelOpen : ''}`}>
        {/* 헤더 */}
        <div className={styles.mouPanelHdr}>
          <div className={styles.mouPanelHdrIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <div className={styles.mouPanelHdrInfo}>
            <div className={styles.mouPanelHdrKicker}>ONEFFICE · AI 문서 초안 생성</div>
            <div className={styles.mouPanelHdrTitle}>MOU 초안 — Enterprise AI / Sovereign AI 협력</div>
            <div className={styles.mouPanelHdrSub}>
              {mouDocFull ? 'MOU 초안 생성 완료 · 검토 후 편집하세요'
                : mouPanelStep <= 1 ? 'ONECHAMBER 참고자료 3건 기반으로 생성 중...'
                : mouPanelStep === 2 ? 'MOU 목차 및 조항 구조 설계 중...'
                : mouPanelStep === 3 ? '조항별 문서 초안 작성 중...'
                : '법적 검토 체크포인트 표시 중...'}
            </div>
          </div>
          <button className={styles.mouPanelClose} onClick={() => setMouPanelOpen(false)}>×</button>
        </div>

        {/* 진행 바 */}
        <AXProgressBar
          value={mouDocFull ? 100 : mouPanelStep === 0 ? 0 : mouPanelStep === 1 ? 20 : mouPanelStep === 2 ? 45 : mouPanelStep === 3 ? 68 : 88}
          gradient={['#60A5FA', '#3B82F6']}
          height={3}
          trackColor="#E0E7FF"
          rounded={false}
        />

        {/* 단계 목록 */}
        <div className={styles.mouSteps}>
          <AXStepProgress
            steps={[
              { id: 1, label: 'ONECHAMBER 참고자료 3건 분석 중...' },
              { id: 2, label: 'MOU 목차 및 조항 구조 설계' },
              { id: 3, label: '조항별 문서 초안 작성' },
              { id: 4, label: '법적 검토 체크포인트 표시' },
            ].map(step => ({
              label: step.label,
              status: mouDocFull || mouPanelStep > step.id ? 'done' : !mouDocFull && mouPanelStep === step.id ? 'active' : 'pending',
            }))}
          />
        </div>

        {/* 문서 본문 */}
        <div className={`${styles.mouDocBody} ${!mouDocFull ? styles.mouDocBodyGenerating : ''}`}>

          {/* 생성 중 오버레이 */}
          {!mouDocFull && (
            <div className={`${styles.mouDocGenerating} ${mouGenFadeOut ? styles.mouDocGeneratingFadeOut : ''}`}>
              <div className={styles.mouGenOrb}>
                <span className={styles.mouGenOrbIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <path d="M8 13h8M8 17h6"/>
                  </svg>
                </span>
              </div>
              <div className={styles.mouGenTitle}>{mouGenTitle}</div>
              <div className={styles.mouGenDesc}>{mouGenDesc}</div>
              <div className={styles.mouGenStatusWrap}>
                <span className={styles.mouGenDot}/>
                <span>{mouGenStatus}</span>
              </div>
            </div>
          )}

          {/* 완성 문서 */}
          {mouDocFull && (
          <div className={`${styles.mouDocPage} ${mouDocReveal ? styles.mouDocPageReveal : ''}`}>
            <div className={styles.mouDocTitle}>Enterprise AI &amp; Sovereign AI 파트너십 MOU 초안</div>
            <div className={styles.mouDocSubtitle}>2026년 6월 · 김민준 · 글로벌 사업개발</div>
            <>
                <div className={styles.mouDocSection}>
                  <div className={styles.mouDocSectionTitle}>1. 양사의 역할과 책임 범위</div>
                  <p className={styles.mouDocP}>본 협력을 위해 갑(Enterprise AI Platform 운영사)은 플랫폼 기술·레퍼런스·PoC 환경을 제공하고, 을(Tokyo Strategic Partners)은 일본 시장 고객사 네트워크·현지 법령 자문·영업 채널을 담당한다. 최종 의사결정 및 계약 체결은 양사 각각의 대표 권한자가 수행한다.</p>
                </div>
                <div className={styles.mouDocSection}>
                  <div className={styles.mouDocSectionTitle}>2. 일본 시장 내 공동 영업 및 PoC 추진 방식</div>
                  <table className={styles.mouDocTable}>
                    <thead><tr><th>단계</th><th>내용</th><th>담당</th></tr></thead>
                    <tbody>
                      <tr><td><b>1단계</b></td><td>공동 고객 발굴 및 니즈 분석</td><td>영업·컨설팅 TF</td></tr>
                      <tr><td><b>2단계</b></td><td>PoC 패키지 설계 및 제안서 작성</td><td>양사 공동</td></tr>
                      <tr><td><b>3단계</b></td><td>파일럿 고객사 1~2곳 PoC 실행</td><td>기술·보안 TF</td></tr>
                      <tr><td><b>4단계</b></td><td>성과 분석 후 정식 파트너십 계약 검토</td><td>양사 대표</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className={styles.mouDocSection}>
                  <div className={styles.mouDocSectionTitle}>3. 고객 데이터 보관 위치 및 보안/컴플라이언스 기준</div>
                  <p className={styles.mouDocP}>모든 고객 데이터는 일본 국내 데이터센터에 보관하며, 개인정보보호법(APPI) 및 금융청 지침을 준수한다. 데이터 접근 권한은 최소 권한 원칙에 따라 관리하며, 분기별 보안 감사 로그를 양사 공동으로 검토한다.</p>
                </div>
                <div className={styles.mouDocSection}>
                  <div className={styles.mouDocSectionTitle}>4. 회계·세무 업무 자동화 영역에서의 주요 레퍼런스</div>
                  <table className={styles.mouDocTable}>
                    <thead><tr><th>레퍼런스</th><th>적용 영역</th><th>성과 지표</th></tr></thead>
                    <tbody>
                      <tr><td><b>국내 중견 제조사 A</b></td><td>ERP 세무·회계 자동화</td><td>업무 시간 42% 절감</td></tr>
                      <tr><td><b>물류사 B (일본 법인)</b></td><td>전자세금계산서 발행 자동화</td><td>오류율 98% 감소</td></tr>
                      <tr><td><b>IT서비스사 C</b></td><td>문서관리·내부감사 자동화</td><td>감사 준비시간 60% 단축</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className={styles.mouDocSection}>
                  <div className={styles.mouDocSectionTitle}>5. 향후 기술 검증 및 공동 웨비나 추진 일정</div>
                  <table className={styles.mouDocTable}>
                    <thead><tr><th>일정</th><th>내용</th><th>형태</th></tr></thead>
                    <tbody>
                      <tr><td>다음 주 화요일</td><td>MOU 초안 검토 화상회의</td><td>온라인</td></tr>
                      <tr><td>2주 이내</td><td>데이터 주권·보안 요건 기술 검토</td><td>기술 TF 미팅</td></tr>
                      <tr><td>7월 2주차</td><td>공동 고객 대상 PoC 제안 발표</td><td>현장/온라인 병행</td></tr>
                      <tr><td>8월 중</td><td>공동 웨비나 — 일본 시장 AI 자동화 트렌드</td><td>온라인 공개</td></tr>
                    </tbody>
                  </table>
                </div>
                <div className={styles.mouDocProActiveBanner}>
                  <b>✦ ProActive 추천:</b> 완성된 MOU 초안을 일본어 회신 메일에 첨부하고, 다음 주 화요일 화상회의 일정을 함께 제안하세요.
                </div>
                <div className={styles.mouDocDisclaimer}>
                  ⚠️ AI 초안 — 법무 검토 전 최종 계약서로 사용 불가 · ONEFFICE에서 편집 가능
                </div>
            </>
          </div>
          )}
        </div>

        {/* 푸터 */}
        <div className={styles.mouPanelFooter}>
          <button className={styles.mouPanelBtn} onClick={() => setMouPanelOpen(false)}>닫기</button>
          {mouDocFull && (
            <>
              <button className={styles.mouPanelBtn} onClick={() => { window.dispatchEvent(new Event('navAddAiTab')); setMouPanelOpen(false); navigate('/oneffice/mou_draft'); }}>ONEFFICE에서 편집하기</button>
              <button className={`${styles.mouPanelBtn} ${styles.mouPanelBtnPrimary}`} onClick={openMouReply}>회신 메일에 첨부하기 →</button>
            </>
          )}
        </div>
      </div>

      {/* ── 보고서 패널 ── */}
      <div className={`${styles.mouPanel} ${reportPanelOpen ? styles.mouPanelOpen : ''}`}>
        {/* 헤더 */}
        <div className={styles.mouPanelHdr}>
          <div className={styles.mouPanelHdrIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="16" y1="13" x2="8" y2="13"/>
              <line x1="16" y1="17" x2="8" y2="17"/>
              <polyline points="10 9 9 9 8 9"/>
            </svg>
          </div>
          <div className={styles.mouPanelHdrInfo}>
            <div className={styles.mouPanelHdrKicker}>ONEFFICE · AI 문서 초안 생성</div>
            <div className={styles.mouPanelHdrTitle}>이번주 미수금 현황 보고서</div>
            <div className={styles.mouPanelHdrSub}>
              {reportDocFull ? '보고서 초안 생성 완료 · 검토 후 편집하세요'
                : reportPanelStep <= 1 ? 'ERP 회계 거래내역 기반으로 생성 중...'
                : reportPanelStep === 2 ? '미수금 거래처 선별·집계 중...'
                : reportPanelStep === 3 ? '보고서 표·요약 구성 중...'
                : '제출용 초안 정리 중...'}
            </div>
          </div>
          <button className={styles.mouPanelClose} onClick={closeReportPanel}>×</button>
        </div>

        {/* 진행 바 */}
        <AXProgressBar
          value={reportDocFull ? 100 : reportPanelStep === 0 ? 0 : reportPanelStep === 1 ? 20 : reportPanelStep === 2 ? 45 : reportPanelStep === 3 ? 70 : 90}
          gradient={['#60A5FA', '#3B82F6']}
          height={3}
          trackColor="#E0E7FF"
          rounded={false}
        />

        {/* 단계 목록 */}
        <div className={styles.mouSteps}>
          <AXStepProgress
            steps={[
              { id: 1, label: 'ERP 회계 거래내역 분석 중...' },
              { id: 2, label: '미수금 거래처 선별 및 집계' },
              { id: 3, label: '보고서 표·요약 구성' },
              { id: 4, label: '제출용 초안 정리' },
            ].map(step => ({
              label: step.label,
              status: reportDocFull || reportPanelStep > step.id ? 'done' : !reportDocFull && reportPanelStep === step.id ? 'active' : 'pending',
            }))}
          />
        </div>

        {/* 문서 본문 */}
        <div className={`${styles.mouDocBody} ${!reportDocFull ? styles.mouDocBodyGenerating : ''}`}>
          {!reportDocFull && (
            <div className={`${styles.mouDocGenerating} ${reportGenFadeOut ? styles.mouDocGeneratingFadeOut : ''}`}>
              <div className={styles.mouGenOrb}>
                <span className={styles.mouGenOrbIcon}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                    <polyline points="14 2 14 8 20 8"/>
                    <path d="M8 13h8M8 17h6"/>
                  </svg>
                </span>
              </div>
              <div className={styles.mouGenTitle}>{reportGenTitle}</div>
              <div className={styles.mouGenDesc}>{reportGenDesc}</div>
              <div className={styles.mouGenStatusWrap}>
                <span className={styles.mouGenDot}/>
                <span>{reportGenStatus}</span>
              </div>
            </div>
          )}
          {reportDocFull && (
            <div className={`${styles.mouDocPage} ${reportDocReveal ? styles.mouDocPageReveal : ''}`}>
              <div className={styles.mouDocTitle}>이번주 미수금 현황 보고서</div>
              <div className={styles.mouDocSubtitle}>2026년 6월 · 김민준 · ERP 회계</div>
              <div className={styles.mouDocSection}>
                <div className={styles.mouDocSectionTitle}>1. 미수금 현황 요약</div>
                <p className={styles.mouDocP}>이번주 기준 미수금이 발생한 거래처는 총 <strong>3곳</strong>, 합계 <strong>7,370,000 JPY</strong>입니다. 이 중 1곳(㈜아사히 전자)은 <strong>연체</strong> 상태로 우선 회수가 필요하며, 2곳은 마감이 임박했거나 처리 중입니다.</p>
              </div>
              <div className={styles.mouDocSection}>
                <div className={styles.mouDocSectionTitle}>2. 거래처별 미수금 내역</div>
                <table className={styles.mouDocTable}>
                  <thead><tr><th>거래처명</th><th>미수금액(JPY)</th><th>마감일</th><th>상태</th></tr></thead>
                  <tbody>
                    <tr><td><strong>㈜아사히 전자</strong></td><td>2,450,000</td><td>2025.08.25</td><td>연체</td></tr>
                    <tr><td><strong>도쿄 물산 주식회사</strong></td><td>1,820,000</td><td>2025.08.28</td><td>마감 임박</td></tr>
                    <tr><td><strong>오사카 상사</strong></td><td>3,100,000</td><td>2025.08.30</td><td>처리 중</td></tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.mouDocSection}>
                <div className={styles.mouDocSectionTitle}>3. 권장 조치</div>
                <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8, color: '#334155' }}>
                  <li>연체 거래처(㈜아사히 전자) 우선 독촉장 발송</li>
                  <li>마감 임박 거래처에 사전 안내 메일 발송</li>
                  <li>회수 완료 후 전자세금계산서 자동 발행</li>
                </ul>
              </div>
              <div className={styles.mouDocProActiveBanner}>
                <strong>✦ ProActive 추천:</strong> 보고서 제출 후 연체 거래처 독촉장 초안과 세금계산서 발행을 함께 처리하세요.
              </div>
              <div className={styles.mouDocDisclaimer}>
                ⚠️ AI 초안 — 제출 전 내용을 검토하세요 · ONEFFICE에서 편집 가능
              </div>
            </div>
          )}
        </div>

        {/* 푸터 */}
        <div className={styles.mouPanelFooter}>
          <button className={styles.mouPanelBtn} onClick={closeReportPanel}>닫기</button>
          {reportDocFull && (
            <button className={`${styles.mouPanelBtn} ${styles.mouPanelBtnPrimary}`} onClick={() => { closeReportPanel(); navigate('/oneffice/report'); }}>
              ONEFFICE에서 편집하기 →
            </button>
          )}
        </div>
      </div>

      {/* ── MOU 회신 메일 모달 ── */}
      <div
        className={`${styles.mouReplyOverlay} ${mouReplyOpen ? styles.mouReplyOverlayOpen : ''}`}
        onClick={e => { if (e.target === e.currentTarget) closeMouReply(); }}
      >
        <div className={styles.mouReplyModal} role="dialog" aria-modal="true" aria-label="MOU 회신 메일 작성">
          {/* 헤더 */}
          <div className={styles.mouReplyHdr}>
            <div className={styles.mouReplyHdrIc}>
              <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4h16v16H4z"/><path d="M22 6l-10 7L2 6"/>
              </svg>
            </div>
            <div className={styles.mouReplyHdrTx}>
              <div className={styles.mouReplyKicker}>AI 회신 작성 · 일본어 파트너 메일</div>
              <div className={styles.mouReplyTitle}>MOU 초안 회신 메일</div>
            </div>
            <button className={styles.mouReplyClose} onClick={closeMouReply} aria-label="닫기">×</button>
          </div>

          {/* 스크롤 영역 */}
          <div className={styles.mouReplyScroll}>
            {/* 수신인·제목 */}
            <div className={styles.mouReplyFields}>
              <div className={styles.mrfRow}>
                <span className={styles.mrfL}>받는사람</span>
                <span className={styles.mrfV}>
                  <AXTagChip
                    label="타카하시 켄지 본부장 · Tokyo Strategic Partners <takahashi@tokyo-strategic.jp>"
                    variant="readonly"
                    background="#EEF2FF"
                    textColor="#3730A3"
                    borderColor="#C7D2FE"
                  />
                </span>
              </div>
              <div className={styles.mrfRow}>
                <span className={styles.mrfL}>제목</span>
                <span className={`${styles.mrfV} ${styles.mrfSubj}`}>Re: Enterprise AI 및 Sovereign AI 협업에 관한 MOU 초안 요청</span>
              </div>
            </div>

            {/* 첨부파일 */}
            <div className={styles.mouReplyAttach}>
              <div className={styles.mraLabel}>첨부파일</div>
              {mouReplyPhase === 'loading' ? (
                <div className={styles.mraConverting}>
                  <span className={styles.mraSpin}/>
                  <span>ONEFFICE 문서를 PDF로 변환하고 있어요...</span>
                </div>
              ) : (
                <div className={styles.mraFile}>
                  <span className={styles.mraFileIc}>PDF</span>
                  <div className={styles.mraFileInfo}>
                    <div className={styles.mraFileName}>MOU_초안_EnterpriseAI_SovereignAI.pdf</div>
                    <div className={styles.mraFileMeta}>PDF 문서 · 312KB · 6개 조항 · ONEFFICE에서 변환</div>
                  </div>
                  <span className={styles.mraFileDone}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                      <path d="M20 6L9 17l-5-5"/>
                    </svg>
                  </span>
                </div>
              )}
            </div>

            {/* AI 노트 */}
            {mouReplyPhase === 'ready' && (
              <div className={styles.mouReplyNote}>
                <span>✦</span>
                <span>AI가 한국어로 편집 가능한 초안을 작성했습니다. 발송 전 일본어 정중체(敬語) 발송본을 함께 확인할 수 있습니다.</span>
              </div>
            )}

            {/* 언어 탭 */}
            {mouReplyPhase === 'ready' && (
              <div className={styles.mouReplyLang}>
                <div>
                  <div className={styles.mouReplyLangTitle}>회신 메일 검토</div>
                  <div className={styles.mouReplyLangDesc}>한국어로 내용을 수정하고, 실제 발송될 일본어 문안을 확인할 수 있습니다.</div>
                </div>
                <div className={styles.mouReplyViewToggle}>
                  <AXSegmentedControl
                    size="sm"
                    variant="outline"
                    value={mouReplyLang}
                    onChange={v => { setMouReplyEditing(false); setMouReplyLang(v as 'ko' | 'ja'); }}
                    activeBackground="#EFF6FF"
                    activeColor="#2563EB"
                    activeBorderColor="#93C5FD"
                    options={[
                      { value: 'ko', label: '편집 · 한국어' },
                      { value: 'ja', label: '발송 · 일본어' },
                    ]}
                  />
                </div>
              </div>
            )}

            {/* 본문 작성 영역 */}
            <div className={styles.mouReplyCompose}>
              {mouReplyPhase === 'loading' ? (
                <div className={styles.mrcWriting}>
                  <span className={styles.mraSpin}/>
                  <span>AI가 회신 초안을 작성하고 있어요...</span>
                </div>
              ) : (
                <>
                  {/* 한국어 패널 */}
                  {mouReplyLang === 'ko' && (
                    <div className={styles.mouReplyPane}>
                      {mouReplyEditing ? (
                        <>
                          <textarea
                            className={styles.mouReplyEditor}
                            value={mouReplyKoText}
                            onChange={e => setMouReplyKoText(e.target.value)}
                            autoFocus
                          />
                          <div className={styles.mouReplyEditNote}>
                            한국어로 수정한 내용은 발송 시 일본어 정중체로 다시 변환됩니다. 일본어 문안은 &apos;발송 · 일본어&apos;에서 확인할 수 있습니다.
                          </div>
                        </>
                      ) : (
                        <div className={styles.mrcBody}>{mouReplyKoText}</div>
                      )}
                      <div className={styles.mrcQuote}>
                        <div className={styles.mrcQuoteHead}>2026년 6월 27일 (금) 09:18, 타카하시 켄지 본부장 &lt;takahashi@tokyo-strategic.jp&gt; 작성:</div>
                        <div className={styles.mrcQuoteBody}>{`김민준 차장님께,

지난주 온라인 미팅에서 귀사의 Enterprise AI Platform 및 Sovereign AI 추진 방향에 대해 상세히 설명해 주셔서 감사합니다. 특히 회계·세무·문서관리·커뮤니케이션 데이터가 하나의 업무 흐름으로 연결되고, AI가 사용자의 다음 업무를 선제적으로 제안한다는 점이 매우 인상적이었습니다.

이에 양사 간 파트너십 가능성을 구체적으로 검토하고자 합니다. 우선 Enterprise AI 및 Sovereign AI 협력을 위한 MOU 초안을 받아볼 수 있을까요?`}</div>
                      </div>
                    </div>
                  )}

                  {/* 일본어 패널 */}
                  {mouReplyLang === 'ja' && (
                    <div className={styles.mouReplyPane}>
                      <div className={styles.mrcBody}>{`高橋 健司 本部長

いつも大変お世話になっております。
Enterprise AI Platform グローバル事業開発担当のキム・ミンジュンでございます。

このたびご依頼いただきました、Enterprise AI および Sovereign AI 協業に関する MOU ドラフトを作成し、添付にてお送りいたします。

ご依頼いただいた以下の項目を中心に、内容を反映しております。

1. 両社の役割および責任範囲
2. 日本市場における共同営業および PoC 推進方法
3. 顧客データの保管場所ならびにセキュリティ／コンプライアンス基準
4. 会計・税務業務自動化領域における主要リファレンス
5. 今後の技術検証および共同ウェビナーの実施スケジュール

特に、日本国内におけるデータ主権を考慮した保管構成と、会計・税務業務自動化に関するリファレンスを整理しております。
MOU の各条項につきましては、貴社内でのご検討内容に応じて柔軟に調整可能です。

差し支えなければ、来週中にオンライン会議を設定し、具体的な協業範囲について協議できればと存じます。
ご都合のよい候補日程をご共有いただけますと幸いです。

ご確認のほど、何卒よろしくお願い申し上げます。

キム・ミンジュン
Enterprise AI Platform
グローバル事業開発`}</div>
                      <div className={styles.mrcQuote}>
                        <div className={styles.mrcQuoteHead}>2026年6月27日（金）09:18、高橋 健司 本部長 &lt;takahashi@tokyo-strategic.jp&gt; より:</div>
                        <div className={styles.mrcQuoteBody}>{`キム・ミンジュン 次長

先週のオンラインミーティングでは、貴社の Enterprise AI Platform および Sovereign AI の推進方針について詳しくご説明いただき、誠にありがとうございました。

つきましては、両社間のパートナーシップの可能性を具体的に検討したく存じます。まずは、Enterprise AI および Sovereign AI の協業に関する MOU ドラフトをご共有いただくことは可能でしょうか。

ご確認のほど、何卒よろしくお願い申し上げます。`}</div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          {/* 푸터 */}
          <div className={styles.mouReplyFooter}>
            <span className={styles.mouReplyHint}>{mouReplyHint}</span>
            <div className={styles.mouReplyActionsGroup}>
              <button className={styles.mouReplyBtn} onClick={closeMouReply}>닫기</button>
              {mouReplyPhase === 'ready' && mouReplyLang === 'ko' && (
                <button
                  className={`${styles.mouReplyBtn} ${styles.mouReplyBtnAiEdit}`}
                  onClick={() => setMouReplyEditing(e => !e)}
                >
                  {mouReplyEditing ? '편집 완료' : '메일 편집'}
                </button>
              )}
              <button
                className={`${styles.mouReplyBtn} ${styles.mouReplyBtnPrimary}`}
                disabled={mouReplyPhase !== 'ready'}
                onClick={sendMouReply}
              >보내기 →</button>
            </div>
          </div>
        </div>
      </div>

      {/* MOU 발송 토스트 */}
      <div className={`${styles.mouReplyToast} ${mouReplyToastVisible ? styles.mouReplyToastShow : ''}`}>
        <span className={styles.mrtIc}>
          <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
            <path d="M20 6L9 17l-5-5"/>
          </svg>
        </span>
        <span>일본어 회신 메일이 발송되었습니다 · MOU 초안 PDF 첨부</span>
      </div>

      {/* ── ProActive FAB ── */}
      {isConvMode && !mouPanelOpen && !mailSidePanelOpen && !docPreviewOpen && !reportPanelOpen && !aiPaDrawerOpen && (
        <button
          className={`${styles.aiPaFab} ${styles.aiPaFabVisible}`}
          type="button"
          onClick={() => setAiPaDrawerOpen(true)}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L13.5 10.5L22 12L13.5 13.5L12 22L10.5 13.5L2 12L10.5 10.5Z"/>
          </svg>
          <span>ProActive 제안</span>
          <span className={styles.aiPaFabCount}>{currentProActiveCards.length}</span>
        </button>
      )}

      {/* ── ProActive Drawer Backdrop ── */}
      <div
        className={`${styles.aiPaDrawerBackdrop} ${aiPaDrawerOpen ? styles.aiPaDrawerBackdropShow : ''}`}
        onClick={() => setAiPaDrawerOpen(false)}
      />

      {/* ── ProActive Drawer ── */}
      <aside
        className={`${styles.aiPaDrawer} ${aiPaDrawerOpen ? styles.aiPaDrawerOpen : ''}`}
        aria-label="ProActive 제안"
      >
        <div className={styles.aiPaDrawerHead}>
          <div className={styles.paDBadge}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L13.5 10.5L22 12L13.5 13.5L12 22L10.5 13.5L2 12L10.5 10.5Z"/>
            </svg>
          </div>
          <div className={styles.aiPaDrawerHtxt}>
            <div className={styles.aiPaDrawerTitle}>ProActive 제안</div>
            <div className={styles.aiPaDrawerSub}>대화방을 벗어나지 않고 다른 업무를 골라 바로 실행하세요</div>
          </div>
          <button
            className={styles.aiPaDrawerClose}
            type="button"
            aria-label="닫기"
            onClick={() => setAiPaDrawerOpen(false)}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div className={styles.aiPaDrawerBody}>
          {currentProActiveCards.map((card, idx) => (
            <ProActiveSlotCard
              key={card.id}
              card={card}
              idx={idx}
              onClick={() => {
                setAiPaDrawerOpen(false);
                if (card.prompt) handlePostSuggestClick({ id: idx, responseKey: card.id, title: card.title, desc: card.desc, actionLabel: card.actionLabel, actionStyle: card.actionStyle, prompt: card.prompt });
              }}
              onSecondaryAction={card.secondaryAction === 'mail-source' ? () => { setAiPaDrawerOpen(false); setMouMailSourceOpen(true); } : undefined}
            />
          ))}
        </div>
      </aside>

      {/* ── MOU 메일 원문 오버레이 ── */}
      {mouMailSourceOpen && (
        <div
          className={styles.mouMailSourceOverlay}
          onClick={() => setMouMailSourceOpen(false)}
        >
          <div className={styles.mouMailSourceModal} onClick={e => e.stopPropagation()}>
            {/* 헤더 */}
            <div className={styles.mouMailSourceHdr}>
              <div className={styles.mouMailSourceIcon}>
                <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/>
                </svg>
              </div>
              <div className={styles.mouMailSourceHdrInfo}>
                <div className={styles.mouMailSourceKicker}>PARTNER MAIL · ORIGINAL</div>
                <div className={styles.mouMailSourceTitle}>Enterprise AI / Sovereign AI 제휴 관련 메일</div>
                <div className={styles.mouMailSourceSub}>수신된 메일 원문을 먼저 표시합니다. 필요하면 한국어 번역으로 전환해 확인할 수 있습니다.</div>
              </div>
              <button className={styles.mouMailSourceClose} onClick={() => setMouMailSourceOpen(false)}>×</button>
            </div>

            {/* 본문 */}
            <div className={styles.mouMailSourceBody}>
              <div className={styles.mouMailSourceHint}>메일 확인 후 실행하면 AI가 메일 요청사항을 브리핑하고 다음 업무를 단계별로 제안합니다.</div>
              <div className={styles.mouMailAiNote}>
                ✦ 일본어 원문을 그대로 보존했습니다. 한국어 번역 탭으로 전환하면 같은 내용을 사용자 언어로 확인할 수 있습니다.
              </div>
              <div className={styles.mouMailSourceMeta}>
                <span className={styles.mouMailMetaLabel}>보낸사람</span>
                <span className={styles.mouMailMetaValue}>타카하시 켄지 본부장 · Tokyo Strategic Partners</span>
                <span className={styles.mouMailMetaLabel}>받은시간</span>
                <span className={styles.mouMailMetaValue}>오늘 09:18 · 일본어 메일</span>
                <span className={styles.mouMailMetaLabel}>제목</span>
                <span className={styles.mouMailMetaValue}>Enterprise AI 및 Sovereign AI 협업에 관한 MOU 초안 요청</span>
              </div>
              <div className={styles.mouMailLangTabs}>
                <AXSegmentedControl
                  size="sm"
                  variant="outline"
                  value={mouMailSourceLang}
                  onChange={v => setMouMailSourceLang(v as 'ko' | 'ja')}
                  activeBackground="#F5F3FF"
                  activeColor="#6D28D9"
                  activeBorderColor="#7C3AED"
                  options={[
                    { value: 'ja', label: '일본어 원문' },
                    { value: 'ko', label: '한국어 번역' },
                  ]}
                />
              </div>
              {mouMailSourceLang === 'ja' ? (
                <div className={`${styles.mouMailOriginalBox} ${styles.mouMailOriginalBoxJa}`}>
                  {`金 民俊 様\n\n先週のオンラインミーティングでは、貴社のEnterprise AI PlatformおよびSovereign AIの推進方針について、詳細にご説明いただき誠にありがとうございました。特に、会計・税務・文書管理・コミュニケーションデータが一つの業務フローとして連携し、AIがユーザーの次の業務を先回りして提案するという点に、大変強い関心を持ちました。\n\n弊社では現在、日本国内の中堅企業のお客様を対象に、会計・税務業務の自動化および社内文書管理体制の改善支援を行っております。最近では、お客様から「日本国内におけるデータ主権を担保しながら、業務自動化を高度化できるAIプラットフォーム」に関する相談が急速に増加しております。\n\nつきましては、両社間のパートナーシップの可能性を具体的に検討したく、まずはEnterprise AIおよびSovereign AI連携に関するMOUドラフトをご共有いただくことは可能でしょうか。可能であれば、以下の内容を含めていただけますと幸いです。\n\n1. 両社の役割および責任範囲\n2. 日本市場における共同営業およびPoC推進方法\n3. 顧客データの保管場所ならびにセキュリティ／コンプライアンス基準\n4. 会計・税務業務自動化領域における主要リファレンス\n5. 今後の技術検証および共同ウェビナーの実施スケジュール\n\nまた、貴社が保有されている関連紹介資料、日本市場への適用可能性があるリファレンス文書、技術仕様書などがございましたら、併せてご共有いただけますでしょうか。社内で確認したうえで、来週中にオンライン会議を設定し、具体的な協業範囲について協議できればと考えております。\n\nご確認のほど、何卒よろしくお願い申し上げます。`.split('\n').map((line, i) => (
                    <span key={i}>{line}<br /></span>
                  ))}
                </div>
              ) : (
                <div className={styles.mouMailOriginalBox}>
                  {`김민준 차장님께,\n\n지난주 온라인 미팅에서 귀사의 Enterprise AI Platform 및 Sovereign AI 추진 방향에 대해 상세히 설명해 주셔서 감사합니다. 특히 회계·세무·문서관리·커뮤니케이션 데이터가 하나의 업무 흐름으로 연결되고, AI가 사용자의 다음 업무를 선제적으로 제안한다는 점이 매우 인상적이었습니다.\n\n당사는 일본 내 중견기업 고객을 대상으로 회계/세무 운영 자동화와 내부 문서관리 체계 개선을 지원하고 있으며, 최근 고객사들로부터 "일본 내 데이터 주권을 보장하면서도 업무 자동화를 고도화할 수 있는 AI 플랫폼"에 대한 문의가 빠르게 증가하고 있습니다.\n\n이에 양사 간 파트너십 가능성을 구체적으로 검토하고자 합니다. 우선 Enterprise AI 및 Sovereign AI 협력을 위한 MOU 초안을 받아볼 수 있을까요? 가능하다면 다음 내용을 포함해 주시면 감사하겠습니다.\n\n1. 양사의 역할과 책임 범위\n2. 일본 시장 내 공동 영업 및 PoC 추진 방식\n3. 고객 데이터 보관 위치 및 보안/컴플라이언스 기준\n4. 회계·세무 업무 자동화 영역에서의 주요 레퍼런스\n5. 향후 기술 검증 및 공동 웨비나 추진 일정\n\n또한 귀사가 보유한 관련 소개자료, 일본 시장 적용 가능성이 있는 레퍼런스 문서, 기술 사양서가 있다면 함께 공유 부탁드립니다. 내부 검토 후 다음 주 중 화상회의를 통해 구체적인 협력 범위를 논의하고 싶습니다.\n\n확인 부탁드리며, 회신 기다리겠습니다.\n\n타카하시 켄지\nTokyo Strategic Partners\n본부장\nTel: +81-3-XXXX-XXXX`.split('\n').map((line, i) => (
                    <span key={i}>{line}<br /></span>
                  ))}
                </div>
              )}
            </div>

            {/* 푸터 */}
            <div className={styles.mouMailSourceActions}>
              <div className={styles.mouMailSourceActionGroup}>
                <button
                  className={styles.mouMailSourceBtnSecondary}
                  onClick={() => setMouMailSourceOpen(false)}
                >닫기</button>
                <button
                  className={styles.mouMailSourceBtnPrimary}
                  onClick={() => {
                    setMouMailSourceOpen(false);
                    const mouCard = PROACTIVE_CARD_QUEUE.find(c => c.id === 'mou-mail');
                    if (mouCard) handleProActiveCardClick(mouCard);
                  }}
                >실행하기 →</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 상환 스케줄 확인 팝업 */}
      <RepaymentScheduleModal open={repaymentScheduleOpen} onClose={() => setRepaymentScheduleOpen(false)} />

    </div>
  );
}
